import { get, off, onChildAdded, onValue, push, ref, remove, runTransaction, serverTimestamp, set, update } from "firebase/database";
import { IBlogComment, IBlogPost, IClubPitch, IClubPitchBooking, IClubPitchSlot, IControl, IFixture, INotification, IPoll, ISupporterRecord, IPlayerStats, IResult, ITicket, ResultInstance, TeamConfig, IUser, AuthPayload, IPlayerNote, ITrainingSchedule, INewsArticle, IPaymentRequest, ITransaction, ClubConfig, IClubControl, IAboutPage, IClubLink, IClubPaymentSetup, IClubMemberPayment, IClubDiscountLink, ITacticalVideo, ITacticsBoard, IPlayerRecord, IPlayerSeasonReport, ITrafficLog, IDiaryAttendanceRequest, IDiaryAttendanceRecipientProjection, IDiaryAttendanceResponse } from "../definitions";
import { auth, database } from "./firebase"
import { v4 } from "uuid";
import { getLeaguesAndDivisions, getRandomClub, getRandomClubName, getRandomPlayerName } from "./randomUtils";
import { getAwardWinners } from "./awards";
import { Preferences } from '@capacitor/preferences';
import { nativeGet, nativeOnChildAdded, nativeOnValue, nativePush, nativeRemove, nativeSet, nativeTransaction, nativeUpdate, shouldUseNativeDatabase } from "./firebase-native-database";
import { callFirebaseFunction } from "./firebaseCallable";
import { applyParentPollVotes } from "./parentPlayerOfWeek";
import { recordJourneyAction } from "./journeyTracking";
import type { ProfilePhotoSlot } from '../definitions';

let archiveTag = '';
let selectedConfig: TeamConfig | null = null;
const senderId = v4(); // Generate a unique sender ID for the service worker
export type DisposableDatasource = () => void;
const messageCallbacks: ((message: INotification) => void)[] = [];
const trafficCallbacks: ((message: ITrafficLog) => void)[] = [];

// Generic cache and callback manager for database paths
type DBCallback<T> = (data: T) => void;

interface DBCache<T> {
  cache: Record<string, T>;
  callbacks: Record<string, DBCallback<T>[]>;
  unsubscribeFns: Record<string, (() => void)[]>;
}

const dbCache: Record<string, DBCache<any>> = {};

/** Drops cached data and listeners when the signed-in identity or access changes. */
export const clearDbCache = () => {
  Object.entries(dbCache).forEach(([key, cache]) => {
    Object.values(cache.unsubscribeFns).flat().forEach(unsubscribe => unsubscribe());
    delete dbCache[key];
  });
};

// Generic method for fetching and subscribing to a database path
/**
 * Pure one-time fetch that bypasses caching and subscription system
 * Use this for bulk data loading where you don't want to trigger or use subscriptions
 */
async function getDbDataOnce<T>(
  path: string,
  refBuilder: () => ReturnType<typeof ref>,
  // Accepted for call-signature compatibility with getDbData (see getResults'
  // fetchFn union). A one-time fetch has no ongoing poll/callback, so these are
  // unused here.
  _callback?: DBCallback<T>,
  _sentinelPath?: string
): Promise<T | null> {
  if (shouldUseNativeDatabase()) {
    try {
      // throwOnError: a failed read must return null (fetch failed) rather than
      // the empty value, so callers can tell "read failed" apart from "node is
      // genuinely missing" and never treat a slow-connection error as empty data.
      const snapshot = await nativeGet(path, { throwOnError: true });
      return snapshot.exists() ? snapshot.val() as T : [] as unknown as T;
    } catch (error) {
      console.error(`🔴 Native one-time fetch ${path} error: ${error}`);
      return null;
    }
  }

  return get(refBuilder())
    .then((snapshot) => snapshot.exists() ? snapshot.val() as T : ([] as unknown as T))
    .catch((error) => {
      console.error(error);
      return null;
    });
}

async function getDbData<T>(
  path: string,
  refBuilder: () => ReturnType<typeof ref>,
  callback?: DBCallback<T>,
  sentinelPath?: string
): Promise<T | null> {
  if (!dbCache[path]) {
    dbCache[path] = {
      cache: {},
      callbacks: {},
      unsubscribeFns: {},
    };
  }
  const cacheObj = dbCache[path];

  // Add callback if provided
  if (callback) {
    if (!cacheObj.callbacks[path]) cacheObj.callbacks[path] = [];
    if (!cacheObj.callbacks[path].includes(callback)) {
      cacheObj.callbacks[path].push(callback);
    }
  }

  // Return cached value if available
  // Return cached value if available
  if (cacheObj.cache[path]) {
    if (callback) callback(cacheObj.cache[path]);
    // Only skip re-subscribing when no callback is involved, OR when a live listener is
    // already active for this path. If the listener was previously torn down (e.g. after
    // a useEffect cleanup) but the cache still has a truthy value, we must fall through
    // to re-establish it — otherwise real-time updates are silently lost.
    const hasActiveListener = (cacheObj.unsubscribeFns[path]?.length ?? 0) > 0;
    if (!callback || hasActiveListener) {
      return Promise.resolve(cacheObj.cache[path]);
    }
    // Fall through to create a new subscription
  }

  // Only subscribe to updates if a callback is provided (for realtime updates)
  if (callback) {
    if (shouldUseNativeDatabase()) {
      // Use native polling for iOS
      const unsubscribe = nativeOnValue(path, (snapshot) => {
        const data = snapshot.exists() ? snapshot.val() as T : ([] as unknown as T);
        cacheObj.cache[path] = data;
        (cacheObj.callbacks[path] || []).forEach(cb => cb(data));
      }, undefined, sentinelPath);

      // Store unsubscribe function
      if (!cacheObj.unsubscribeFns[path]) cacheObj.unsubscribeFns[path] = [];
      cacheObj.unsubscribeFns[path].push(unsubscribe);
    } else {
      // Use WebSocket-based onValue for web/Android
      const unsubscribeFn = onValue(refBuilder(), (snapshot) => {
        const data = snapshot.exists() ? snapshot.val() as T : ([] as unknown as T);
        cacheObj.cache[path] = data;
        (cacheObj.callbacks[path] || []).forEach(cb => cb(data));
      });
      // Store unsubscribe function so it can be cleaned up when all callbacks are removed
      if (!cacheObj.unsubscribeFns[path]) cacheObj.unsubscribeFns[path] = [];
      cacheObj.unsubscribeFns[path].push(unsubscribeFn);
    }
  }

  // Fetch initial data (one-time fetch if no callback, or initial data for subscription)
  if (shouldUseNativeDatabase()) {
    try {
      // throwOnError: never cache or broadcast the empty sentinel for a failed
      // read — a slow-connection error is not proof that the node is empty.
      const snapshot = await nativeGet(path, { throwOnError: true });
      const data = snapshot.exists() ? snapshot.val() as T : [] as unknown as T;
      cacheObj.cache[path] = data;
      // When callback is provided, nativeOnValue/onValue is the single source of truth
      // for updates to avoid out-of-order callback races.
      if (callback) callback?.(data);
      return data;
    } catch (error) {
      console.error(`🔴 Native ${path} error: ${error}`);
      return null;
    }
  }

  // Return a promise for initial fetch
  return get(refBuilder())
    .then((snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val() as T;
        cacheObj.cache[path] = data;
        // When callback is provided, onValue already delivers initial + live data.
        if (callback) callback?.(data);
        return data;
      } else {
        cacheObj.cache[path] = ([] as unknown as T);
        if (callback) callback?.([] as unknown as T);
        return ([] as unknown as T);
      }
    })
    .catch((error) => {
      console.error(error);
      return null;
    });
}

/**
 * Recursively removes undefined values from an object so Firebase Realtime DB
 * never receives undefined (which causes exceptions).
 */
export function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .map(stripUndefined)
      .filter((item) => item !== undefined) as unknown as T;
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndefined(v)])
    ) as T;
  }
  return value;
}

// Generic method for setting data to a database path
async function setDbData<T>(
  path: string,
  data: T,
  refBuilder: () => ReturnType<typeof ref>
): Promise<void> {
  const cleanData = stripUndefined(data);

  if (shouldUseNativeDatabase()) {
    try {
      await nativeSet(path, cleanData);
      return;
    } catch (error) {
      console.error(`🔴 Native set ${path} error: ${error}`);
      throw error;
    }
  }

  return set(refBuilder(), cleanData);
}

async function updateDbData<T extends object>(
  path: string,
  data: T,
  refBuilder: () => ReturnType<typeof ref>
): Promise<void> {
  const cleanData = stripUndefined(data);

  if (shouldUseNativeDatabase()) {
    try {
      await nativeUpdate(path, cleanData);
      return;
    } catch (error) {
      console.error(`🔴 Native update ${path} error: ${error}`);
      throw error;
    }
  }

  await update(refBuilder(), cleanData);
}

// Generic method for removing data from a database path
async function removeDbData(
  path: string,
  refBuilder: () => ReturnType<typeof ref>
): Promise<void> {
  if (shouldUseNativeDatabase()) {
    try {
      await nativeRemove(path);
      return;
    } catch (error) {
      console.error(`🔴 Native remove ${path} error: ${error}`);
      throw error;
    }
  }

  return remove(refBuilder());
}

// Generic method for pushing data to a database path
async function pushDbData<T>(
  path: string,
  data: T,
  refBuilder: () => ReturnType<typeof ref>
): Promise<string> {
  const cleanData = stripUndefined(data);

  if (shouldUseNativeDatabase()) {
    try {
      const key = await nativePush(path, cleanData);
      return key;
    } catch (error) {
      console.error(`🔴 Native push ${path} error: ${error}`);
      throw error;
    }
  }

  const pushRef = push(refBuilder(), cleanData);
  return pushRef.key || '';
}

// Generic unsubscribe method for any path and callback
function unsubscribeDbData<T>(path: string, callback: DBCallback<T>) {
  const cacheObj = dbCache[path];
  if (cacheObj && cacheObj.callbacks[path]) {
    const idx = cacheObj.callbacks[path].indexOf(callback);
    if (idx !== -1) {
      cacheObj.callbacks[path].splice(idx, 1);
    }

    // If no more callbacks, clean up all listeners (native and web)
    if (cacheObj.callbacks[path].length === 0 && cacheObj.unsubscribeFns[path]?.length > 0) {
      cacheObj.unsubscribeFns[path].forEach(unsub => unsub());
      cacheObj.unsubscribeFns[path] = [];
    }
  }
}

let _auth: AuthPayload & { dbUser?: IUser | null } = { user: null, loading: true, isAdmin: false };
export const setLoggedInUser = (auth: AuthPayload, dbUser?: IUser | null) => {
  _auth = { ...auth, dbUser };
}

export const getLoggedInUserName = () => {
  // Email/password accounts often have no displayName; fall back so audit lines always identify the actor.
  let liveUser: { displayName?: string | null; email?: string | null } | null = null;
  try {
    liveUser = auth?.currentUser ?? null;
  } catch {
    liveUser = null;
  }
  return _auth.user?.displayName
    || _auth.dbUser?.displayName
    || liveUser?.displayName
    || _auth.user?.email
    || _auth.dbUser?.email
    || liveUser?.email
    || '';
}

export const getLoggedInUser = () => {
  return _auth;
}

export const addMessageCallback = (callback: (message: INotification) => void) => {
  if (messageCallbacks.includes(callback)) return; // Avoid duplicates
  messageCallbacks.push(callback);
};
export const removeMessageCallback = (callback: (message: INotification) => void) => {
  const index = messageCallbacks.indexOf(callback);
  if (index !== -1) {
    messageCallbacks.splice(index, 1);
  }
};

export const addTrafficCallback = (callback: (message: ITrafficLog) => void) => {
  if (trafficCallbacks.includes(callback)) return;
  trafficCallbacks.push(callback);
};

export const removeTrafficCallback = (callback: (message: ITrafficLog) => void) => {
  const index = trafficCallbacks.indexOf(callback);
  if (index !== -1) {
    trafficCallbacks.splice(index, 1);
  }
};

// Send an update to the live feed channel
export function auditUpdate(notification: INotification) {
  const db = getDatabase();
  const path = "auditLogs";
  pushDbData(path, {
    ...notification,
    timestamp: serverTimestamp(),
  }, () => ref(db, path));
}

export function trafficUpdate(notification: Omit<ITrafficLog, 'timestamp'> & { timestamp?: string }) {
  const db = getDatabase();
  const path = "trafficLogs";
  pushDbData(path, {
    ...notification,
    timestamp: serverTimestamp(),
  }, () => ref(db, path));
}

// Send an update to the live feed channel
export function clearAudit() {
  const db = getDatabase();
  const path = "auditLogs";
  const feedRef = ref(db, path);
  return removeDbData(path, () => feedRef);
}

// Subscribe to live feed updates (returns unsubscribe function)
export async function subscribeToAudit(): Promise<DisposableDatasource> {
  const db = getDatabase();
  const path = "auditLogs";

  if (shouldUseNativeDatabase()) {
    // Use native polling for iOS
    return nativeOnChildAdded(path, (snapshot) => {
      const data = snapshot.val() as INotification;
      messageCallbacks.forEach(cb => cb(data));
    });
  } else {
    // Use WebSocket-based onChildAdded for web/Android
    const feedRef = ref(db, path);
    const handler = onChildAdded(feedRef, (snapshot) => {
      const data = snapshot.val() as INotification;
      messageCallbacks.forEach(cb => cb(data));
    });
    // Return unsubscribe function
    return () => off(feedRef, "child_added", handler);
  }
}

export async function subscribeToTraffic(): Promise<DisposableDatasource> {
  const db = getDatabase();
  const path = "trafficLogs";

  if (shouldUseNativeDatabase()) {
    return nativeOnChildAdded(path, (snapshot) => {
      const data = snapshot.val() as ITrafficLog;
      trafficCallbacks.forEach(cb => cb(data));
    });
  }

  const feedRef = ref(db, path);
  const handler = onChildAdded(feedRef, (snapshot) => {
    const data = snapshot.val() as ITrafficLog;
    trafficCallbacks.forEach(cb => cb(data));
  });
  return () => off(feedRef, "child_added", handler);
}

const getAuditPath = (): string => `auditLogs`;
// Subscribe to live feed updates (returns unsubscribe function)
export async function snapshotAudit(callback?: (updates: INotification[]) => void): Promise<INotification[]> {
  const path = getAuditPath();
  const messages: INotification[] = [];
  const data = await getDbData<INotification[]>(
    path,
    () => ref(getDatabase(), path),
    callback
  ) as INotification[];
  // Fetch initial data
  Object.entries(data).forEach(([key, msg]) => {
    messages.push(msg);
  });
  return messages;
}

export async function snapshotTraffic(callback?: (updates: ITrafficLog[]) => void): Promise<ITrafficLog[]> {
  const path = "trafficLogs";
  const messages: ITrafficLog[] = [];
  const data = await getDbData<ITrafficLog[]>(
    path,
    () => ref(getDatabase(), path),
    callback
  ) as ITrafficLog[];
  Object.entries(data).forEach(([, msg]) => {
    messages.push(msg);
  });
  return messages;
}

export const setSelectedTeam = (config: TeamConfig) => {
  selectedConfig = config;
  // Persist to Preferences so it survives app close/reopen
  // Prefer the canonical route to avoid an extra redirect on the next launch.
  if (config && config.name) {
    const teamNameUrl = config.staticUrl?.trim() || config.name.toLowerCase().replaceAll(' ', '_');
    Preferences.set({ key: 'SelectedTeam', value: teamNameUrl }).catch(err =>
      console.error('Failed to persist selected team:', err)
    );
  }
};

export const getSelectedConfig = () => {
  return selectedConfig;
};

export const clearSelectedTeam = () => {
  selectedConfig = null;
  // Clear from Preferences so it doesn't persist across logins
  Preferences.remove({ key: 'SelectedTeam' }).catch(err =>
    console.error('Failed to clear selected team from Preferences:', err)
  );
};

export const getArchiveTag = () => {
  return archiveTag;
};

export const setArchiveTag = (tag: string) => {
  archiveTag = tag;
};

const getDatabase = () => {
  return database;
};


export const writeUniqueShortcutUrl = async (url: string): Promise<void> => {
  if (!selectedConfig) {
    console.error('Cannot write shortcut URL without selected team');
    return;
  }
  auditUpdate({
    action: "Adding Shortcut URL",
    user: senderId,
    details: `Adding shortcut URL for ${selectedConfig.name} by ${getLoggedInUserName()}`,
    teamName: selectedConfig.name,
    timestamp: new Date().toISOString(),
  });
  const db = getDatabase();
  const path = `config/urls/${url}`;
  await setDbData(path, selectedConfig.uuid, () => ref(db, path));
};

export const deleteUniqueShortcutUrl = async (url: string): Promise<void> => {
  if (!selectedConfig) {
    console.error('Cannot delete shortcut URL without selected team');
    return;
  }
  auditUpdate({
    action: "Deleting Shortcut URL",
    user: senderId,
    details: `Deleting shortcut URL for ${selectedConfig.name} by ${getLoggedInUserName()}`,
    teamName: selectedConfig.name,
    timestamp: new Date().toISOString(),
  });
  const db = getDatabase();
  const path = `config/urls/${url}`;
  await removeDbData(path, () => ref(db, path));
};

const getUrlsChildPath = (url: string): string => `config/urls/${url}`;
export const getUniqueShortcutForUrl = (url: string): Promise<string | null> => {
  const path = getUrlsChildPath(url);
  return getDbData<string | null>(
    path,
    () => ref(getDatabase(), path),
  );
};

const getUrlsPath = (): string => `config/urls`;
export const getUniqueShortcutUrls = (): Promise<Record<string, string>> => {
  const path = getUrlsPath();
  return getDbData<Record<string, string>>(
    path,
    () => ref(getDatabase(), path),
  ) as Promise<Record<string, string>>;
}

async function transactPlayers(
  path: string,
  transform: (current: Record<string, IPlayerStats> | null) => Record<string, IPlayerStats>
): Promise<Record<string, IPlayerStats>> {
  if (shouldUseNativeDatabase()) return nativeTransaction(path, transform);
  const transaction = await runTransaction(ref(getDatabase(), path), transform, { applyLocally: false });
  if (!transaction.committed) throw new Error('Player transaction was not committed');
  return transaction.snapshot.val() ?? {};
}

export const recordParentPollVotes = async (poll: IPoll, teamUuid: string): Promise<void> => {
  // The web SDK first runs transactions against its local cache, which may still be empty.
  const players = await transactPlayers(`teams/${teamUuid}/players`, current => current ? applyParentPollVotes(poll, current) : {});
  applyParentPollVotes(poll, players);
};

export const getParentPollForSettlement = async (pollId: string, teamUuid: string): Promise<IPoll | null> => {
  const path = `teams/${teamUuid}/polls/${pollId}`;
  if (shouldUseNativeDatabase()) {
    const snapshot = await nativeGet(path);
    if (!snapshot.exists()) throw new Error('Unable to read the parent poll for settlement');
    return snapshot.val() as IPoll;
  }
  const snapshot = await get(ref(getDatabase(), path));
  return snapshot.exists() ? snapshot.val() as IPoll : null;
};

export const writePlayers = async (players: Record<string, IPlayerStats>, isArchive?: boolean, archiveTag?: string): Promise<void> => {
  if (!selectedConfig) {
    console.error('Cannot write players without selected team');
    return;
  }
  auditUpdate({
    action: "Updating Players",
    user: senderId,
    details: `Updating players for ${selectedConfig.name} by ${getLoggedInUserName()}`,
    teamName: selectedConfig.name,
    timestamp: new Date().toISOString(),
  });
  const db = getDatabase();
  const path = `teams/${selectedConfig.uuid}/${isArchive ? `archive/${archiveTag}/` : ''}players`;
  if (isArchive) {
    await setDbData(path, players, () => ref(db, path));
  } else {
    await transactPlayers(path, current => {
      const updated = stripUndefined(players);
      Object.entries(updated).forEach(([name, player]) => {
        // Poll settlement owns these fields; stale result/player edits must not overwrite them.
        const latest = current?.[name];
        if (!latest) return;
        delete player.parentVotes;
        delete player.parentVotePolls;
        if (latest?.parentVotes !== undefined) player.parentVotes = latest.parentVotes;
        if (latest?.parentVotePolls) player.parentVotePolls = latest.parentVotePolls;
      });
      return updated;
    });
  }
};

export const writeSupporter = async (supporterName: string,
  uid: string, email: string, teamUuid?: string
): Promise<void> => {
  const uuid = teamUuid || selectedConfig?.uuid;
  if (!uuid) {
    console.error('Cannot write supporter without selected team');
    throw new Error('Cannot write supporter without selected team');
  }
  const db = getDatabase();
  const path = `teams/${uuid}/supporters/${uid}`;
  await updateDbData(path, { uid, supporterName, email, lastUpdated: new Date().toISOString() }, () => ref(db, path));
};

export const updateSupporter = (uid: string, info: Partial<ISupporterRecord>): Promise<void> => {
  if (!selectedConfig) {
    console.error('Cannot update supporter without selected team');
    return Promise.reject(new Error('Cannot update supporter without selected team'));
  }
  const db = getDatabase();
  const path = `teams/${selectedConfig.uuid}/supporters/${uid}`;
  return updateDbData(path, info, () => ref(db, path));
};

export const deleteSupporter = (uid: string, teamUuid?: string): Promise<void> => {
  const uuid = teamUuid || selectedConfig?.uuid;
  if (!uuid) {
    console.error('Cannot delete supporter without selected team');
    return Promise.reject(new Error('Cannot delete supporter without selected team'));
  }
  const db = getDatabase();
  const path = `teams/${uuid}/supporters/${uid}`;
  return removeDbData(path, () => ref(db, path));
};

const isSupporterRecord = (value: unknown): value is ISupporterRecord => {
  if (!value || typeof value !== 'object') return false;
  const supporter = value as Partial<ISupporterRecord>;
  return Boolean(
    supporter.uid?.trim()
    || supporter.supporterName?.trim()
    || supporter.email?.trim()
    || supporter.coach
    || supporter.manager
    || supporter.clubAdmin
    || (typeof supporter.player === 'string' && supporter.player.trim())
    || (Array.isArray(supporter.player) && supporter.player.some(player => player?.trim()))
  );
};

export const filterSupporterRecords = (
  data: Record<string, ISupporterRecord> | null | undefined
): Record<string, ISupporterRecord> => Object.fromEntries(
  Object.entries(data ?? {}).filter(([, supporter]) => isSupporterRecord(supporter))
);

/**
 * Read a single preference value stored under a supporter's record.
 * Path: teams/{uuid}/supporters/{uid}/prefs/{key}
 * Returns null if not found or on error.
 */
export const getSupporterPref = async (uuid: string, uid: string, key: string): Promise<string | null> => {
  if (!uuid || !uid || !key) return null;
  const db = getDatabase();
  const path = `teams/${uuid}/userPrefs/${uid}/${key}`;
  const legacyPath = `teams/${uuid}/supporters/${uid}/prefs/${key}`;
  try {
    const result = await getDbDataOnce<string>(path, () => ref(db, path));
    // getDbDataOnce returns [] as sentinel when path doesn't exist
    if (result !== null && result !== undefined && !(Array.isArray(result) && result.length === 0)) return result;
    const legacyResult = await getDbDataOnce<string>(legacyPath, () => ref(db, legacyPath));
    if (legacyResult === null || legacyResult === undefined || (Array.isArray(legacyResult) && legacyResult.length === 0)) return null;
    return legacyResult;
  } catch {
    return null;
  }
};

/**
 * Write a single preference value under a supporter's record.
 * Path: teams/{uuid}/supporters/{uid}/prefs/{key}
 */
export const setSupporterPref = (uuid: string, uid: string, key: string, value: string): void => {
  if (!uuid || !uid || !key) return;
  const db = getDatabase();
  const path = `teams/${uuid}/userPrefs/${uid}/${key}`;
  setDbData(path, value, () => ref(db, path)).catch(err =>
    console.error(`Failed to save supporter pref ${key}:`, err)
  );
};

export const writeTeams = (teams: Record<string, string[]>, isArchive?: boolean, archiveTag?: string) => {
  if (!selectedConfig) {
    console.error('Cannot write teams without selected team');
    return;
  }
  auditUpdate({
    action: "Updating Opposition Teams",
    user: senderId,
    details: `Updating opposition teams for ${selectedConfig.name} by ${getLoggedInUserName()}`,
    teamName: selectedConfig.name,
    timestamp: new Date().toISOString(),
  });
  const db = getDatabase();
  const path = `teams/${selectedConfig.uuid}/${isArchive ? `archive/${archiveTag}/` : ''}teams`;
  setDbData(path, teams, () => ref(db, path));
};

export const auditFormGuideMerge = (details: string) => {
  if (!selectedConfig) return;
  auditUpdate({
    action: "Merging Form Guide Teams",
    user: senderId,
    details: `${details} (by ${getLoggedInUserName()})`,
    teamName: selectedConfig.name,
    timestamp: new Date().toISOString(),
  });
};

export const writeConfiguredTeam = async (team: TeamConfig): Promise<void> => {
  if (!team.uuid) {
    console.error('Cannot write configured team without UUID');
    return;
  }
  auditUpdate({
    action: "Adding/Updating Team",
    user: senderId,
    details: `Adding/Updating team for ${team.name} by ${team.owner}`,
    teamName: team.name,
    timestamp: new Date().toISOString(),
  });
  const db = getDatabase();
  const path = `config/teams/${team.uuid}`;
  await setDbData(path, team, () => ref(db, path));
};

export const writeConfiguredLeagueAndDivision = (league?: string, division?: string) => {
  if (!league || !division) {
    return;
  }
  const db = getDatabase();
  const path = `config/leagues/${league.toLowerCase().trim()}/divisions/${division.toLowerCase().trim()}`;
  setDbData(path, true, () => ref(db, path));
};

const getLeaguesPath = (): string => `config/leagues`;
export const getConfiguredLeagueAndDivisions = async (): Promise<Record<string, { divisions: Record<string, boolean> }> | null> => {
  const path = getLeaguesPath();
  let leagues = await getDbData<Record<string, { divisions: Record<string, boolean> }>>(
    path,
    () => ref(getDatabase(), path),
  );
  if (!leagues) leagues = {};
  const dummyLeagues = getLeaguesAndDivisions();
  Object.entries(dummyLeagues).forEach(([league, divisions]) => {
    if (!leagues[league]) {
      leagues[league] = { divisions: {} };
    }
    divisions.forEach((division) => {
      leagues[league].divisions[division] = true;
    });
  });
  return leagues;
};

export const getTrainingSchedules = (uid: string | undefined, schedulesCallback?: ((schedules: Record<string, ITrainingSchedule> | null) => void)): Promise<Record<string, ITrainingSchedule> | null> => {
  const path = `teams/${uid ?? selectedConfig?.uuid}/trainingSchedules`;
  return getDbData<Record<string, ITrainingSchedule> | null>(
    path,
    () => ref(getDatabase(), path),
    schedulesCallback
  );
};

export const getTeamDataOnce = (uuid: string): Promise<Record<string, unknown> | null> => {
  const path = `teams/${uuid}`;
  return getDbDataOnce<Record<string, unknown>>(path, () => ref(getDatabase(), path));
};

export const unsubscribeTrainingSchedules = (callback: (schedules: Record<string, ITrainingSchedule> | null) => void, uid?: string) => {
  const path = `teams/${uid ?? selectedConfig?.uuid}/trainingSchedules`;
  unsubscribeDbData<Record<string, ITrainingSchedule> | null>(path, callback);
};

export const writeTrainingSchedule = (uid: string | undefined, schedule: ITrainingSchedule) => {
  const db = getDatabase();
  const path = `teams/${uid ?? selectedConfig?.uuid}/trainingSchedules/${schedule.id}`;
  setDbData(path, schedule, () => ref(db, path));
};

export const deleteTrainingSchedule = (uid: string | undefined, scheduleId: string) => {
  const db = getDatabase();
  const path = `teams/${uid ?? selectedConfig?.uuid}/trainingSchedules/${scheduleId}`;
  removeDbData(path, () => ref(db, path));
};

const fcmTokens = new Map<string, string>(); // Store FCM tokens per user

export const writeFcmToken = (uid: string, newFcmToken: string) => {
  fcmTokens.set(uid, newFcmToken);
  const db = getDatabase();
  const path = `fcmTokens/${uid}/${newFcmToken}`;
  setDbData(path, newFcmToken, () => ref(db, path));
};

export const removeCurrentFcmToken = (uid: string) => {
  const token = fcmTokens.get(uid);
  if (!token) {
    console.warn(`No FCM token found for user ${uid}`);
    return;
  }
  const db = getDatabase();
  const path = `fcmTokens/${uid}/${token}`;
  removeDbData(path, () => ref(db, path));
  fcmTokens.delete(uid);
};

const hasValidDbKey = (value: unknown): value is string => {
  return typeof value === 'string' && value.trim().length > 0 && value !== 'undefined' && value !== 'null';
};

export const writeUser = (user: IUser) => {
  if (!hasValidDbKey(user.uid)) {
    console.error('Cannot write user without a valid uid', user);
    return Promise.resolve();
  }
  const db = getDatabase();
  const path = `users/${user.uid}`;
  // Merge rather than set(): callers routinely pass a partially-populated record, and a
  // destructive write here silently wipes followIds/requestIds/ticketIds/isScout.
  // Pass an explicit null to clear a field.
  return updateDbData(path, { ...user } as unknown as Record<string, unknown>, () => ref(db, path));
};

const isMissingIdentityValue = (value?: string | null): boolean =>
  !value || !value.trim() || value.trim().toLowerCase() === 'unknown';

/**
 * Fills in displayName/email/photoURL on an existing user record from the Firebase Auth
 * profile. writeUser() only ever runs inside an explicit sign-in action, so a record left
 * incomplete by a partial sign-in stays nameless forever and shows as "Unknown" in follow
 * requests. Uses update() so followIds/requestIds and any other fields are never touched,
 * and never overwrites a value the user already has.
 */
export const backfillUserIdentity = async (
  uid: string,
  identity: { displayName?: string | null; email?: string | null; photoURL?: string | null },
  existing: IUser | null,
): Promise<boolean> => {
  if (!hasValidDbKey(uid)) return false;

  const updates: Record<string, unknown> = {};
  if (isMissingIdentityValue(existing?.displayName) && !isMissingIdentityValue(identity.displayName)) {
    updates.displayName = identity.displayName!.trim();
  }
  if (isMissingIdentityValue(existing?.email) && identity.email?.trim()) {
    updates.email = identity.email.trim();
  }
  if (isMissingIdentityValue(existing?.photoURL) && identity.photoURL?.trim()) {
    updates.photoURL = identity.photoURL.trim();
  }
  if (Object.keys(updates).length === 0) return false;

  updates.uid = uid;
  if (!existing?.createdAt) updates.createdAt = new Date().toISOString();

  const db = getDatabase();
  const path = `users/${uid}`;
  await updateDbData(path, updates, () => ref(db, path));
  return true;
};

export const getUser = (uid: string, userCallback?: ((user: IUser | null) => void), oneTimeFetch: boolean = false) => {
  const path = `users/${uid}`;
  const fetchFn = (oneTimeFetch && !userCallback) ? getDbDataOnce : getDbData;
  return fetchFn<IUser | null>(
    path,
    () => ref(getDatabase(), path),
    userCallback
  );
};

export const unsubscribeUser = (callback: (user: IUser | null) => void, uid: string) => {
  const path = `users/${uid}`;
  unsubscribeDbData<IUser | null>(path, callback);
};

export const deleteUserAccountData = async (uid: string): Promise<void> => {
  const db = getDatabase();
  await Promise.all([
    removeDbData(`users/${uid}`, () => ref(db, `users/${uid}`)),
    removeDbData(`fcmTokens/${uid}`, () => ref(db, `fcmTokens/${uid}`)),
  ]);
};

export const getFollowRequests = async (
  uuid: string,
  followRequestsUpdated?: (requests: Record<string, boolean> | null) => void,
  oneTimeFetch: boolean = false
): Promise<Record<string, boolean> | null> => {
  const path = `teams/${uuid}/followRequests`;
  const fetchFn = (oneTimeFetch && !followRequestsUpdated) ? getDbDataOnce : getDbData;
  return fetchFn<Record<string, boolean>>(
    path,
    () => ref(getDatabase(), path),
    followRequestsUpdated
  );
};

export const unsubscribeFollowRequests = (callback: (requests: Record<string, boolean> | null) => void, uuid: string) => {
  const path = `teams/${uuid}/followRequests`;
  unsubscribeDbData<Record<string, boolean> | null>(path, callback);
};

export const deleteTeamRequest = (teamUuid: string, userUid: string) => {
  if (!hasValidDbKey(teamUuid) || !hasValidDbKey(userUid)) {
    console.error('Cannot delete follow request without valid ids', { teamUuid, userUid });
    return Promise.resolve();
  }
  const db = getDatabase();
  const path = `teams/${teamUuid}/followRequests/${userUid}`;
  return removeDbData(path, () => ref(db, path));
};

export const writeTeamRequest = (teamUuid: string, userUid: string, approved: boolean) => {
  if (!hasValidDbKey(teamUuid) || !hasValidDbKey(userUid)) {
    console.error('Cannot write follow request without valid ids', { teamUuid, userUid });
    return Promise.resolve();
  }
  const db = getDatabase();
  const path = `teams/${teamUuid}/followRequests/${userUid}`;
  return setDbData(path, approved, () => ref(db, path));
};

export const writeControl = async (control: IControl, teamUuid?: string): Promise<void> => {
  const targetUuid = teamUuid || selectedConfig?.uuid || control.uuid;
  if (!targetUuid) {
    console.error('Cannot write control without selected team UUID');
    return;
  }
  const targetTeamName = selectedConfig?.name || control.teamName || 'team';
  auditUpdate({
    action: "Adding/Updating Control",
    user: senderId,
    details: `Adding/Updating control for ${targetTeamName} by ${getLoggedInUserName()}`,
    teamName: targetTeamName,
    timestamp: new Date().toISOString(),
  });
  const db = getDatabase();
  const path = `teams/${targetUuid}/control`;
  await updateDbData(path, control, () => ref(db, path));
  writeConfiguredLeagueAndDivision(control.league, control.division);
};

export const updateControlFields = async (fields: Partial<IControl>, teamUuid?: string): Promise<void> => {
  const targetUuid = teamUuid || selectedConfig?.uuid || fields.uuid;
  if (!targetUuid) {
    console.error('Cannot update control fields without selected team UUID');
    return;
  }
  const db = getDatabase();
  await updateDbData(`teams/${targetUuid}/control`, fields, () => ref(db, `teams/${targetUuid}/control`));
  if (fields.league !== undefined || fields.division !== undefined) {
    writeConfiguredLeagueAndDivision(fields.league, fields.division);
  }
};

export const writePlayerProfilePhoto = async (
  teamUuid: string, playerName: string, photo: string,
  slot: ProfilePhotoSlot = 'profilePhoto',
): Promise<void> => {
  const targetUuid = teamUuid || selectedConfig?.uuid || '';
  const isSafeKey = (value: string) => hasValidDbKey(value) && !/[.#$\[\]\/\u0000-\u001f\u007f]/.test(value);
  if (!isSafeKey(targetUuid)) {
    throw new Error('Cannot save a profile photo because the team could not be identified.');
  }
  if (!isSafeKey(playerName)) {
    throw new Error('Cannot save a profile photo for this player. Player names cannot contain . # $ [ ] or /.');
  }
  const db = getDatabase();
  const path = `teams/${targetUuid}/control/players/${playerName}`;
  await updateDbData(path, { [slot]: photo || null }, () => ref(db, path));
};

export const writeResults = async (results: ResultInstance[], isArchive?: boolean, archiveTag?: string, options?: { managementAction: boolean }): Promise<void> => {
  if (!selectedConfig) {
    throw new Error('Cannot write results without selected team');
  }
  const teamUuid = selectedConfig.uuid;
  auditUpdate({
    action: "Updating Results",
    user: senderId,
    details: `Updating results for ${selectedConfig.name} by ${getLoggedInUserName()}`,
    teamName: selectedConfig.name,
    timestamp: new Date().toISOString(),
  });
  const db = getDatabase();
  // Callers can pass plain IResult objects or holes at runtime despite the declared type
  const cleanIResults: IResult[] = (results as Array<ResultInstance | IResult | null | undefined>)
    .filter((result): result is ResultInstance | IResult => !!result)
    .map((result) => {
      const maybeWithSerializer = result as ResultInstance & { toDbObject?: () => IResult };
      return typeof maybeWithSerializer.toDbObject === 'function'
        ? maybeWithSerializer.toDbObject()
        : (result as unknown as IResult);
    });
  const path = `teams/${selectedConfig.uuid}/${isArchive ? `archive/${archiveTag}/` : ''}results`;
  await setDbData(path, cleanIResults, () => ref(db, path));
  if (options?.managementAction) recordJourneyAction('results_saved', 'success', teamUuid);
};

export const writeResultFeedback = async (
  result: Pick<IResult, 'date' | 'team'>,
  feedback: { likes?: string[]; comments?: Record<string, string> | null }
): Promise<void> => {
  if (!selectedConfig) throw new Error('Cannot update result feedback without selected team');
  const path = `teams/${selectedConfig.uuid}/${archiveTag ? `archive/${archiveTag}/` : ''}results`;
  const stored = await getDbDataOnce<Record<string, IResult | null>>(path, () => ref(getDatabase(), path));
  const entry = Object.entries(stored ?? {}).find(([, candidate]) =>
    candidate?.date === result.date && candidate.team === result.team);
  if (!entry) throw new Error('The result is unavailable or you do not have permission to view it');
  const resultPath = `${path}/${entry[0]}`;
  await updateDbData(resultPath, feedback, () => ref(getDatabase(), resultPath));
};

/**
 * Updates only the attending/notAttending fields for a single fixture at a
 * specific index without rewriting the entire fixtures array.  Using a
 * targeted update prevents stale-data overwrites when multiple clients
 * write attendance concurrently.
 */
export const updateFixtureAttendance = async (
  idx: number,
  attending: string[],
  notAttending: string[]
): Promise<void> => {
  if (!selectedConfig) {
    console.error('Cannot update fixture attendance without selected team');
    return;
  }
  const path = `teams/${selectedConfig.uuid}/fixtures/${idx}`;
  // Firebase/RTDB deletes a key when the value is null; pass null for empty
  // arrays so the key is cleanly removed rather than stored as an empty node.
  const payload: Record<string, string[] | null> = {
    attending: attending.length > 0 ? attending : null,
    notAttending: notAttending.length > 0 ? notAttending : null,
  };

  if (shouldUseNativeDatabase()) {
    await nativeUpdate(path, payload);
    return;
  }

  const { update } = await import('firebase/database');
  const db = getDatabase();
  await update(ref(db, path), payload);
};

export const writeFixtures = async (results: IFixture[], isArchive?: boolean, archiveTag?: string, options?: { managementAction: boolean }): Promise<void> => {
  if (!selectedConfig) {
    throw new Error('Cannot write fixtures without selected team');
  }
  const teamUuid = selectedConfig.uuid;
  auditUpdate({
    action: "Updating Fixtures",
    user: senderId,
    details: `Updating fixtures for ${selectedConfig.name} by ${getLoggedInUserName()}`,
    teamName: selectedConfig.name,
    timestamp: new Date().toISOString(),
  });
  const db = getDatabase();
  const path = `teams/${selectedConfig.uuid}/${isArchive ? `archive/${archiveTag}/` : ''}fixtures`;
  const fixturesWithIds = results.map((fixture) => ({
    ...fixture,
    id: fixture.id ?? v4(),
  }));
  await setDbData(path, fixturesWithIds, () => ref(db, path));
  if (options?.managementAction) recordJourneyAction('fixtures_saved', 'success', teamUuid);
};

export const writeNotes = (team: string, note: string) => {
  if (!selectedConfig) {
    console.error('Cannot write notes without selected team');
    return;
  }
  const db = getDatabase();
  const path = `teams/${selectedConfig.uuid}/notes/${team}`;
  setDbData(path, note, () => ref(db, path));
};

export const addPlayerNote = (player: string, note: IPlayerNote): string => {
  if (!selectedConfig) {
    console.error('Cannot add player note without selected team');
    return '';
  }
  const db = getDatabase();
  const noteId = v4();
  const path = `teams/${selectedConfig.uuid}/playerNotes/${player}/${noteId}`;
  setDbData(path, note, () => ref(db, path));
  return noteId;
};

export const updatePlayerNote = (player: string, noteId: string, note: IPlayerNote) => {
  if (!selectedConfig) {
    console.error('Cannot update player note without selected team');
    return;
  }
  const db = getDatabase();
  const path = `teams/${selectedConfig.uuid}/playerNotes/${player}/${noteId}`;
  setDbData(path, note, () => ref(db, path));
};

export const deletePlayerNote = (player: string, noteId: string) => {
  if (!selectedConfig) {
    console.error('Cannot delete player note without selected team');
    return;
  }
  const db = getDatabase();
  const path = `teams/${selectedConfig.uuid}/playerNotes/${player}/${noteId}`;
  removeDbData(path, () => ref(db, path));
};

const getPlayerNotesPath = (player: string): string => `teams/${selectedConfig?.uuid}/playerNotes/${player}`;
export const getPlayerNotes = (player: string): Promise<Record<string, IPlayerNote> | null> => {
  if (!selectedConfig) {
    return Promise.resolve(null);
  }
  const path = getPlayerNotesPath(player);
  return getDbData<Record<string, IPlayerNote> | null>(
    path,
    () => ref(getDatabase(), path),
  );
};

const getPlayerSeasonReportPath = (player: string, uuid?: string): string => `teams/${uuid ?? selectedConfig?.uuid}/playerSeasonReports/${player}`;
export const getPlayerSeasonReport = (player: string, uuid?: string): Promise<IPlayerSeasonReport | null> => {
  if (!uuid && !selectedConfig) {
    return Promise.resolve(null);
  }
  const path = getPlayerSeasonReportPath(player, uuid);
  return getDbDataOnce<IPlayerSeasonReport | null>(path, () => ref(getDatabase(), path));
};

export const writePlayerSeasonReport = (player: string, report: IPlayerSeasonReport, uuid?: string): Promise<void> => {
  if (!uuid && !selectedConfig) {
    return Promise.resolve();
  }
  const path = getPlayerSeasonReportPath(player, uuid);
  return setDbData(path, report, () => ref(getDatabase(), path));
};

export const writeAllNotes = (notes: Record<string, string>, isArchive?: boolean, archiveTag?: string) => {
  if (!selectedConfig) {
    console.error('Cannot write notes without selected team');
    return;
  }
  auditUpdate({
    action: "Updating Notes",
    user: senderId,
    details: `Updating notes for ${selectedConfig.name} by ${getLoggedInUserName()}`,
    teamName: selectedConfig.name,
    timestamp: new Date().toISOString(),
  });
  const db = getDatabase();
  const path = `teams/${selectedConfig.uuid}/${isArchive ? `archive/${archiveTag}/` : ''}notes`;
  setDbData(path, notes, () => ref(db, path));
};
export const writeBlogs = (blogs: IBlogPost[]) => {
  if (!selectedConfig) {
    console.error('Cannot write blogs without selected team');
    return;
  }
  auditUpdate({
    action: "Updating Blog Posts",
    user: senderId,
    details: `Updating blog posts for ${selectedConfig.name} by ${getLoggedInUserName()}`,
    teamName: selectedConfig.name,
    timestamp: new Date().toISOString(),
  });
  const db = getDatabase();
  const path = `teams/${selectedConfig.uuid}/blogPosts`;
  setDbData(path, blogs, () => ref(db, path));
};

export const toggleBlogPostLike = async (postIndex: number, uid: string, liked: boolean, displayName?: string): Promise<void> => {
  if (!selectedConfig) return;
  const db = getDatabase();
  const path = `teams/${selectedConfig.uuid}/blogPosts/${postIndex}/likes/${uid}`;
  const namePath = `teams/${selectedConfig.uuid}/blogPosts/${postIndex}/likeNames/${uid}`;
  if (liked) {
    await setDbData(path, true, () => ref(db, path));
    if (displayName) await setDbData(namePath, displayName, () => ref(db, namePath));
  } else {
    await removeDbData(path, () => ref(db, path));
    await removeDbData(namePath, () => ref(db, namePath));
  }
};

export const addBlogPostComment = async (postIndex: number, comment: IBlogComment): Promise<void> => {
  if (!selectedConfig) return;
  const db = getDatabase();
  const path = `teams/${selectedConfig.uuid}/blogPosts/${postIndex}/comments`;
  const comments = await getDbDataOnce<IBlogComment[]>(path, () => ref(db, path)) ?? [];
  const updated = Array.isArray(comments) ? [...comments, comment] : [...Object.values(comments), comment];
  await setDbData(path, updated, () => ref(db, path));
};

export const writeArchiveTag = (archiveTags: string[]) => {
  if (!selectedConfig) {
    console.error('Cannot write archive tag without selected team');
    return;
  }
  auditUpdate({
    action: "Updating Archive Tags",
    user: senderId,
    details: `Updating archive tags for ${selectedConfig.name} by ${getLoggedInUserName()}`,
    teamName: selectedConfig.name,
    timestamp: new Date().toISOString(),
  });
  const db = getDatabase();
  const path = `teams/${selectedConfig.uuid}/archive/tags`;
  setDbData(path, archiveTags, () => ref(db, path));
};

export const removeTicket = (ticket: ITicket) => {
  auditUpdate({
    action: "Removing Ticket",
    user: senderId,
    details: `Removing ticket ${ticket.id}`,
    timestamp: new Date().toISOString(),
  });
  const db = getDatabase();
  const path = `tickets/${ticket.id}`;
  removeDbData(path, () => ref(db, path));
};

export const writeTicket = (ticket: ITicket) => {
  auditUpdate({
    action: ticket.id ? "Updating Ticket" : "Creating Ticket",
    user: senderId,
    details: `Ticket ${ticket.id || "new"} for ${ticket.subject} by ${getLoggedInUserName()}`,
    timestamp: new Date().toISOString(),
  });
  const db = getDatabase();
  const path = `tickets/${ticket.id}`;
  setDbData(path, ticket, () => ref(db, path));
};

export const removeTeam = () => {
  if (!selectedConfig) return;
  const teamsPath = `teams/${selectedConfig.uuid}`;
  const configTeamsPath = `config/teams/${selectedConfig.uuid}`;
  const archivePath = `archive/${selectedConfig.uuid}`;
  Promise.all([
    removeDbData(teamsPath, () => ref(getDatabase(), teamsPath)),
    removeDbData(configTeamsPath, () => ref(getDatabase(), configTeamsPath)),
    removeDbData(archivePath, () => ref(getDatabase(), archivePath)),
  ]).then(async () => {
    if (!selectedConfig) return;
    await Preferences.remove({ key: 'SelectedTeam' });
    auditUpdate({
      action: "Removing Team",
      user: senderId,
      details: `Removing team ${selectedConfig.name} by ${selectedConfig.owner}`,
      teamName: selectedConfig.name,
      timestamp: new Date().toISOString(),
    });
  }).catch((error) => {
    console.error("Error removing team:", error);
  });
};

export const clearData = () => {
  if (!selectedConfig) return;
  const fixturesPath = `teams/${selectedConfig.uuid}/fixtures`;
  const resultsPath = `teams/${selectedConfig.uuid}/results`;
  const playersPath = `teams/${selectedConfig.uuid}/players`;
  const teamsPath = `teams/${selectedConfig.uuid}/teams`;
  const notesPath = `teams/${selectedConfig.uuid}/notes`;
  Promise.all([
    removeDbData(fixturesPath, () => ref(getDatabase(), fixturesPath)),
    removeDbData(resultsPath, () => ref(getDatabase(), resultsPath)),
    removeDbData(playersPath, () => ref(getDatabase(), playersPath)),
    removeDbData(teamsPath, () => ref(getDatabase(), teamsPath)),
    removeDbData(notesPath, () => ref(getDatabase(), notesPath)),
  ]).then(() => {
    if (!selectedConfig) return;
    auditUpdate({
      action: "Clearing Team Data",
      user: senderId,
      details: `Clearing data for team ${selectedConfig.name} by ${selectedConfig.owner}`,
      teamName: selectedConfig.name,
      timestamp: new Date().toISOString(),
    });
  }).catch((error) => {
    console.error("Error clearing team data:", error);
  });
};


/**
 * Writes a snapshot of control.players (photo/number/DOB metadata) to the archive
 * so that departed players still show their details when viewing old seasons.
 */
const writeArchivedPlayerRecords = (tag: string) => {
  if (!selectedConfig) return;
  const db = getDatabase();
  const path = `teams/${selectedConfig.uuid}/archive/${tag}/playerRecords`;
  // Read control directly (no archive prefix) so we always get the live player metadata
  const controlPath = `teams/${selectedConfig.uuid}/control`;
  return getDbDataOnce<IControl>(controlPath, () => ref(db, controlPath)).then(control => {
    if (control?.players) {
      setDbData(path, control.players, () => ref(db, path));
    }
  });
};

/**
 * Snapshots any coach-authored end-of-season player reports so they remain
 * viewable when browsing an archived season.
 */
const writeArchivedPlayerSeasonReports = (tag: string) => {
  if (!selectedConfig) return;
  const db = getDatabase();
  const path = `teams/${selectedConfig.uuid}/archive/${tag}/playerSeasonReports`;
  const liveReportsPath = `teams/${selectedConfig.uuid}/playerSeasonReports`;
  return getDbDataOnce<Record<string, IPlayerSeasonReport>>(liveReportsPath, () => ref(db, liveReportsPath)).then(reports => {
    if (reports) {
      setDbData(path, reports, () => ref(db, path));
    }
  });
};

export const writeArchive = (archiveTags: string[]) => {
  const archiveTag = archiveTags[archiveTags.length - 1]; // Get the last tag as the current archive
  let capturedPlayers: Record<string, IPlayerStats> | null = null;
  Promise.all([
    writeArchiveTag(archiveTags),
    // Archive current fixtures so the old season shows its full schedule
    getFixtures().then(fixtures => { if (fixtures) writeFixtures(fixtures, true, archiveTag) }),
    getResults().then(results => { if (results) writeResults(results, true, archiveTag) }),
    getPlayers().then(players => {
      capturedPlayers = players;
      if (players) return writePlayers(players, true, archiveTag);
    }),
    getTeams().then(teams => { if (teams) writeTeams(teams, true, archiveTag) }),
    getNotes().then(notes => { if (notes) writeAllNotes(notes, true, archiveTag) }),
    // Snapshot control.players (photo/number/DOB) so departed players keep their details
    writeArchivedPlayerRecords(archiveTag),
    writeArchivedPlayerSeasonReports(archiveTag),
  ]).then(async () => {
    writeFixtures([]); // Clear fixtures for the new season
    writeResults([]); // Clear results for the new season
    writeTeams({}); // Clear team groupings for the new season
    // Reset player stats for the new season: player names carry forward but all
    // cumulative stats (goals, assists, games, etc.) are zeroed out. Only the
    // profile image URL is preserved.
    if (capturedPlayers) {
      const resetPlayers: Record<string, IPlayerStats> = {};
      Object.keys(capturedPlayers).forEach(name => {
        resetPlayers[name] = {
          goals: 0,
          assists: 0,
          games: 0,
          braces: 0,
          hatricks: 0,
          cleanSheets: 0,
          parentVotes: 0,
          profile: capturedPlayers![name].profile,
        };
      });
      const path = `teams/${selectedConfig?.uuid}/players`;
      await setDbData(path, resetPlayers, () => ref(getDatabase(), path));
    }
    // Notes carry forward as they remain relevant
  }).then(() => {
    // Dispatch event instead of reload for iOS compatibility
    window.dispatchEvent(new CustomEvent('archive-completed'));
  }).catch((error) => {
    console.error("Error writing archive:", error);
  });
};

const getArchiveTagsPath = (uuid?: string): string => `teams/${uuid || selectedConfig?.uuid}/archive/tags`;
export const getArchiveTags = (uuid?: string): Promise<string[] | null> => {
  const path = getArchiveTagsPath(uuid);
  return getDbData<string[] | null>(
    path,
    () => ref(getDatabase(), path),
  );
};

/**
 * Fetches the snapshot of control.players (photo/number/DOB) that was saved
 * when the given season was archived. Returns null if no snapshot exists.
 */
export const getArchivedPlayerRecords = (tag: string, uuid?: string): Promise<Record<string, IPlayerRecord> | null> => {
  const teamUuid = uuid || selectedConfig?.uuid;
  if (!teamUuid || !tag) return Promise.resolve(null);
  const path = `teams/${teamUuid}/archive/${tag}/playerRecords`;
  const db = getDatabase();
  return getDbDataOnce<Record<string, IPlayerRecord>>(path, () => ref(db, path));
};

const getNotesPath = (): string => `teams/${selectedConfig?.uuid}/${archiveTag ? `archive/${archiveTag}/` : ''}notes`;
export const getNotes = (): Promise<Record<string, string> | null> => {
  const notesPath = getNotesPath();
  return getDbData<Record<string, string> | null>(
    notesPath,
    () => ref(getDatabase(), notesPath),
  );
}

const getConfigTeamsPath = (): string => `config/teams`;
export const getConfiguredTeams = async (oneTimeFetch: boolean = false, includeGeneratedDummies: boolean = true): Promise<Record<string, TeamConfig> | null> => {
  const path = getConfigTeamsPath();
  const fetchFn = oneTimeFetch ? getDbDataOnce : getDbData;
  let teams = await fetchFn<Record<string, TeamConfig> | null>(
    path,
    () => ref(getDatabase(), path),
  );
  if (!teams) teams = {};
  if (!includeGeneratedDummies) return teams;
  const toSlug = (value: string) => value.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'team';
  const usedStaticUrls = new Set(
    Object.values(teams)
      .map((team) => team.staticUrl?.toLowerCase().trim())
      .filter((slug): slug is string => Boolean(slug))
  );
  const ensureUniqueSlug = (base: string, suffix: string) => {
    const root = toSlug(base);
    let candidate = `${root}-${suffix}`;
    let i = 2;
    while (usedStaticUrls.has(candidate)) {
      candidate = `${root}-${suffix}-${i}`;
      i += 1;
    }
    usedStaticUrls.add(candidate);
    return candidate;
  };
  const dummyTeamNames: string[] = [];
  // create a dummy team for each team inversing the results
  // limit to 10 teams to avoid flooding the database
  Object.entries(teams).filter(t => !t[0].includes('dummy')).slice(0, 9).forEach(([teamName, teamConfig], idx) => {
    let generated = getRandomClubName(idx);
    while (dummyTeamNames.includes(generated.name)) {
      idx++;
      generated = getRandomClubName(idx);
    }
    teams[`${teamName}_dummy`] = {
      ...teamConfig,
      name: `${generated.name}`,
      uuid: `${teamConfig.uuid}_dummy`,
      staticUrl: ensureUniqueSlug(generated.name, 'dummy'),
      clubId: undefined,
      country: generated.country,
      county: generated.county,
      town: generated.town,
      league: generated.league ?? '',
      division: generated.division ?? '',
      email: '',
      owner: (generated.owner ?? 'System'),
      ownerUid: (generated.owner ?? 'System') + '_dummy',
      badge: teams[`${teamConfig.uuid}_dummy`]?.badge || '',
      // badge: `https://picsum.photos/seed/${teamName}/200`,
    };
    dummyTeamNames.push(generated.name);
    generated = getRandomClubName(idx + 100);
    while (dummyTeamNames.includes(generated.name)) {
      idx++;
      generated = getRandomClubName(idx + 100);
    }
    teams[`${teamName}_dummy_partial`] = {
      ...teamConfig,
      name: `${generated.name}`,
      uuid: `${teamConfig.uuid}_dummy_partial`,
      staticUrl: ensureUniqueSlug(generated.name, 'dummy-partial'),
      clubId: undefined,
      country: generated.country,
      county: generated.county,
      town: generated.town,
      league: generated.league ?? '',
      division: generated.division ?? '',
      email: '',
      owner: generated.owner ?? 'System',
      ownerUid: (generated.owner ?? 'System') + '_dummy_partial',
      badge: teams[`${teamConfig.uuid}_dummy_partial`]?.badge || '',
      // badge: `https://picsum.photos/seed/${teamName}/200`, // lets try to make this more team badge like
    };
    dummyTeamNames.push(generated.name);
    generated = getRandomClubName(idx + 150);
    while (dummyTeamNames.includes(generated.name)) {
      idx++;
      generated = getRandomClubName(idx + 150);
    }
    teams[`${teamName}_dummy_inversed`] = {
      ...teamConfig,
      name: `${generated.name}`,
      uuid: `${teamConfig.uuid}_dummy_inversed`,
      staticUrl: ensureUniqueSlug(generated.name, 'dummy-inversed'),
      clubId: undefined,
      country: generated.country,
      county: generated.county,
      town: generated.town,
      league: generated.league ?? '',
      division: generated.division ?? '',
      email: '',
      owner: generated.owner ?? 'System',
      ownerUid: (generated.owner ?? 'System') + '_dummy_inversed',
      badge: teams[`${teamConfig.uuid}_dummy_inversed`]?.badge || '',
      // badge: `https://picsum.photos/seed/${teamName}/200`, // lets try to make this more team badge like
    };
  });
  return teams;
}

// Maps a caller's callback to the internal listener wrapper so unsubscribeConfiguredTeams
// can remove the exact same listener registered by subscribeConfiguredTeams.
const configuredTeamsWrappers = new WeakMap<
  (teams: Record<string, TeamConfig>) => void,
  (rawTeams: Record<string, TeamConfig> | null) => void
>();

/**
 * Live-subscribes to config/teams (new teams added, existing teams renamed/edited, etc.).
 * Re-runs the full getConfiguredTeams fetch (incl. generated demo teams) on every change
 * so callers (e.g. the Teams list page) update without needing a page reload.
 */
export const subscribeConfiguredTeams = (
  callback: (teams: Record<string, TeamConfig>) => void,
  includeGeneratedDummies: boolean = true
): void => {
  const path = getConfigTeamsPath();
  let wrapper = configuredTeamsWrappers.get(callback);
  if (!wrapper) {
    wrapper = () => {
      void getConfiguredTeams(true, includeGeneratedDummies).then(teams => callback(teams ?? {}));
    };
    configuredTeamsWrappers.set(callback, wrapper);
  }
  void getDbData<Record<string, TeamConfig> | null>(path, () => ref(getDatabase(), path), wrapper);
};

export const unsubscribeConfiguredTeams = (callback: (teams: Record<string, TeamConfig>) => void): void => {
  const wrapper = configuredTeamsWrappers.get(callback);
  if (!wrapper) return;
  unsubscribeDbData<Record<string, TeamConfig> | null>(getConfigTeamsPath(), wrapper);
  configuredTeamsWrappers.delete(callback);
};

// --- Path helpers ---
function getFixturesPath(uuid?: string): string {
  return `teams/${uuid || selectedConfig?.uuid}/${archiveTag ? `archive/${archiveTag}/` : ''}fixtures`;
}
function getTeamsPath(): string {
  return `teams/${selectedConfig?.uuid}/${archiveTag ? `archive/${archiveTag}/` : ''}teams`;
}
function getResultsPath(uuid?: string): string {
  return `teams/${uuid || selectedConfig?.uuid}/${archiveTag ? `archive/${archiveTag}/` : ''}results`;
}
function getBlogPostsPath(): string {
  return `teams/${selectedConfig?.uuid}/blogPosts`;
}

// --- Sentinel helpers (iOS native polling optimisation) ---
// A tiny `meta/...` node holds a server timestamp bumped by a Cloud Function on
// every write to the watched node. On native (iOS) the poller reads this cheap
// sentinel and only re-fetches the large node when it changes. Returns undefined
// when the sentinel does not apply (e.g. archived data, which is immutable, or
// when no team uuid is available) so the caller falls back to a full poll.
function getResultsSentinelPath(uuid?: string): string | undefined {
  if (archiveTag) return undefined;
  const teamUuid = uuid || selectedConfig?.uuid;
  return teamUuid ? `meta/results/${teamUuid}` : undefined;
}
function getFixturesSentinelPath(uuid?: string): string | undefined {
  if (archiveTag) return undefined;
  const teamUuid = uuid || selectedConfig?.uuid;
  return teamUuid ? `meta/fixtures/${teamUuid}` : undefined;
}
function getBlogPostsSentinelPath(): string | undefined {
  const teamUuid = selectedConfig?.uuid;
  return teamUuid ? `meta/blogPosts/${teamUuid}` : undefined;
}
function getNewsArticlesSentinelPath(): string {
  return 'meta/newsArticles';
}
function getSupportersSentinelPath(uuid?: string): string | undefined {
  const teamUuid = uuid || selectedConfig?.uuid;
  return teamUuid ? `meta/supporters/${teamUuid}` : undefined;
}
function getPlayersSentinelPath(uuid?: string): string | undefined {
  if (archiveTag) return undefined;
  const teamUuid = uuid || selectedConfig?.uuid;
  return teamUuid ? `meta/players/${teamUuid}` : undefined;
}
function getPollsPath(): string {
  return `teams/${selectedConfig?.uuid}/polls`;
}
function getControlPath(uuid?: string): string {
  return `teams/${uuid || selectedConfig?.uuid}/control`;
}
function getSupportersPath(uuid?: string): string {
  return `teams/${uuid || selectedConfig?.uuid}/supporters`;
}
function getPlayersPath(uuid?: string): string {
  return `teams/${uuid || selectedConfig?.uuid}/${archiveTag ? `archive/${archiveTag}/` : ''}players`;
}

function getUsersPath(): string {
  return `users/`;
}


// --- Generic subscribe/unsubscribe ---
export const getUsers = (
  callback?: (fixtures: IUser[]) => void
): Promise<IUser[] | null> => {
  const path = getUsersPath();
  return getDbData<IUser[]>(
    path,
    () => ref(getDatabase(), path),
    callback
  );
};

// --- Generic subscribe/unsubscribe ---
export const getFixtures = (
  callback?: (fixtures: IFixture[]) => void,
  uuid?: string
): Promise<IFixture[] | null> => {
  const path = getFixturesPath(uuid);
  return getDbData<IFixture[]>(
    path,
    () => ref(getDatabase(), path),
    callback,
    getFixturesSentinelPath(uuid)
  );
};
export const unsubscribeFixtures = (callback: (fixtures: IFixture[]) => void) => {
  const path = getFixturesPath();
  unsubscribeDbData<IFixture[]>(path, callback);
};

export const getTeams = (
  callback?: (teams: Record<string, string[]>) => void
): Promise<Record<string, string[]> | null> => {
  const path = getTeamsPath();
  return getDbData<Record<string, string[]>>(
    path,
    () => ref(getDatabase(), path),
    callback
  );
};
export const unsubscribeTeams = (callback: (teams: Record<string, string[]>) => void) => {
  const path = getTeamsPath();
  unsubscribeDbData<Record<string, string[]>>(path, callback);
};

export const getNewsArticles = (
  callback?: (articles: Record<string, INewsArticle>) => void
): Promise<Record<string, INewsArticle> | null> => {
  const path = `newsArticles/`;
  return getDbData<Record<string, INewsArticle>>(
    path,
    () => ref(getDatabase(), path),
    callback,
    getNewsArticlesSentinelPath()
  );
}

export const updateArticle = (article: INewsArticle): Promise<void> => {
  const path = `newsArticles/${article.id}`;
  return setDbData(path, article, () => ref(getDatabase(), path));
};

export const deleteArticle = (articleId: string): Promise<void> => {
  const path = `newsArticles/${articleId}`;
  return removeDbData(path, () => ref(getDatabase(), path));
};

export const getResults = async (
  callback?: (results: ResultInstance[]) => void,
  uuid?: string,
  oneTimeFetch: boolean = false
): Promise<ResultInstance[] | null> => {
  const isDummy = uuid?.includes('_dummy');
  const isInversed = uuid?.includes('_inversed');
  const isPartial = uuid?.includes('_partial');
  const path = getResultsPath(uuid?.replace('_dummy', '').replace('_partial', '').replace('_inversed', ''));
  const convertResults = (results: ResultInstance[]): ResultInstance[] => {
    if (!isDummy) return results;
    return results.map((result, idx) => {
      // in all cases mask player names and opposition team name
      const currentName = result.team;
      result.team = getRandomClub(selectedConfig?.name, idx);
      result.report = result.report?.replaceAll(currentName, result.team);
      if (result.played) {

        for (let i = 0; i < result.played?.length; i++) {
          const currentName = result.played[i];
          const newName = getRandomPlayerName(currentName);
          result.played[i] = newName;
          result.report = result.report?.replaceAll(currentName, newName);
        }
      }
      if (result.assists) {
        for (let i = 0; i < result.assists.length; i++) {
          result.assists[i] = getRandomPlayerName(result.assists[i]);
        }
      }
      if (result.scorers) {
        for (let i = 0; i < result.scorers.length; i++) {
          result.scorers[i] = getRandomPlayerName(result.scorers[i]);
        }
      }
      if (result.cleanSheet) {
        for (let i = 0; i < result.cleanSheet.length; i++) {
          result.cleanSheet[i] = getRandomPlayerName(result.cleanSheet[i]);
        }
      }
      if (result.attackers) {
        for (let i = 0; i < result.attackers.length; i++) {
          result.attackers[i] = getRandomPlayerName(result.attackers[i]);
        }
      }
      if (result.midfielders) {
        for (let i = 0; i < result.midfielders.length; i++) {
          result.midfielders[i] = getRandomPlayerName(result.midfielders[i]);
        }
      }
      if (result.defenders) {
        for (let i = 0; i < result.defenders.length; i++) {
          result.defenders[i] = getRandomPlayerName(result.defenders[i]);
        }
      }
      if (result.goalKeepers) {
        for (let i = 0; i < result.goalKeepers.length; i++) {
          result.goalKeepers[i] = getRandomPlayerName(result.goalKeepers[i]);
        }
      }
      if (result.otherAwards) {
        Object.keys(result.otherAwards).forEach((key) => {
          const winners = getAwardWinners(result.otherAwards![key]).map(getRandomPlayerName);
          result.otherAwards![key] = Array.isArray(result.otherAwards![key]) ? winners : (winners[0] ?? '');
        });
      }
      if (result.captain) {
        result.captain = getRandomPlayerName(result.captain);
      }
      if (result.playerOfTheMatch) {
        result.playerOfTheMatch = getRandomPlayerName(result.playerOfTheMatch);
      }
      if (!isInversed && !isPartial) return result;
      if (isPartial && Math.random() > 0.5) return undefined; // randomly return some results as is
      if (isInversed) {
        // inverse the result and other details
        const parts = result.score.split('-');
        result.score = `${parts[1]}-${parts[0]}`;
        if (result.result === 'W') result.result = 'L';
        else if (result.result === 'L') result.result = 'W';
        const goalsFor = parseInt(parts[1]);
        if (!result.scorers) result.scorers = [];
        if (result.scorers.length !== goalsFor) {
          // adjust scorers to match the new score
          if (result.scorers.length < goalsFor) {
            for (let i = result.scorers.length; i < goalsFor; i++) {
              result.scorers.push(getRandomPlayerName(`Player ${i + 1}`));
            }
          } else {
            result.scorers = result.scorers.slice(0, goalsFor);
          }
        }
      }
      return {
        ...result,
      } as ResultInstance;
    }).filter(r => r !== undefined) as ResultInstance[];
  };

  const fetchFn = (oneTimeFetch && !callback) ? getDbDataOnce : getDbData;

  return fetchFn<ResultInstance[]>(
    path,
    () => ref(getDatabase(), path),
    (res) => callback ? (callback(convertResults(res))) : undefined,
    getResultsSentinelPath(uuid?.replace('_dummy', '').replace('_partial', '').replace('_inversed', ''))
  );
};
export const unsubscribeResults = (callback: (results: ResultInstance[]) => void, uuid?: string) => {
  const path = getResultsPath(uuid);
  unsubscribeDbData<ResultInstance[]>(path, callback);
};

export const getBlogPosts = (
  callback?: (posts: IBlogPost[]) => void
): Promise<IBlogPost[] | null> => {
  const path = getBlogPostsPath();
  return getDbData<IBlogPost[]>(
    path,
    () => ref(getDatabase(), path),
    callback,
    getBlogPostsSentinelPath()
  );
};
export const unsubscribeBlogPosts = (callback: (posts: IBlogPost[]) => void) => {
  const path = getBlogPostsPath();
  unsubscribeDbData<IBlogPost[]>(path, callback);
};

export const getPolls = (
  callback?: (polls: IPoll[]) => void
): Promise<IPoll[] | null> => {
  const path = getPollsPath();

  // Helper function to normalize polls (ensure all required properties exist)
  const normalizePolls = (polls: IPoll[]): IPoll[] => {
    return polls.map(poll => ({
      ...poll,
      votes: poll.votes || {},
      settings: poll.settings || { showResults: 'always', anonymous: false, notifyTeam: false, restrictToPlayers: false }
    }));
  };

  // Wrap the callback to convert object to array and normalize
  const wrappedCallback = callback
    ? (data: IPoll[] | Record<string, IPoll> | null) => {
      if (!data) {
        callback([]);
      } else if (Array.isArray(data)) {
        callback(normalizePolls(data));
      } else {
        // Convert object to array (polls are stored as object keyed by poll.id)
        callback(normalizePolls(Object.values(data)));
      }
    }
    : undefined;

  return getDbData<IPoll[] | Record<string, IPoll>>(
    path,
    () => ref(getDatabase(), path),
    wrappedCallback as any
  ).then(data => {
    if (!data) return [];
    if (Array.isArray(data)) return normalizePolls(data);
    // Convert object to array and normalize
    return normalizePolls(Object.values(data));
  });
};

export const unsubscribePolls = (callback: (polls: IPoll[]) => void) => {
  const path = getPollsPath();
  unsubscribeDbData<IPoll[]>(path, callback);
};

export const writePolls = async (polls: IPoll[]): Promise<void> => {
  if (!selectedConfig) {
    console.error('Cannot write polls without selected team');
    return;
  }
  auditUpdate({
    action: "Updating Polls",
    user: senderId,
    details: `Updating polls for ${selectedConfig.name} by ${getLoggedInUserName()}`,
    teamName: selectedConfig.name,
    timestamp: new Date().toISOString(),
  });
  const db = getDatabase();
  const path = getPollsPath();
  await setDbData(path, polls, () => ref(db, path));
};

export const writePoll = async (poll: IPoll): Promise<void> => {
  if (!selectedConfig) {
    console.error('Cannot write poll without selected team');
    return;
  }
  const db = getDatabase();
  const path = `${getPollsPath()}/${poll.id}`;
  await setDbData(path, poll, () => ref(db, path));
};

// Partial update so a client's stale local poll copy can't clobber votes another client just cast.
export const updatePollStatus = async (pollId: string, status: IPoll['status']): Promise<void> => {
  if (!selectedConfig) {
    console.error('Cannot update poll without selected team');
    return;
  }
  const db = getDatabase();
  const path = `${getPollsPath()}/${pollId}`;
  await updateDbData(path, { status }, () => ref(db, path));
};

export const deletePoll = async (pollId: string): Promise<void> => {
  if (!selectedConfig) {
    console.error('Cannot delete poll without selected team');
    return;
  }
  auditUpdate({
    action: "Deleting Poll",
    user: senderId,
    details: `Deleting poll ${pollId} for ${selectedConfig.name} by ${getLoggedInUserName()}`,
    teamName: selectedConfig.name,
    timestamp: new Date().toISOString(),
  });
  const db = getDatabase();
  const path = `${getPollsPath()}/${pollId}`;
  await removeDbData(path, () => ref(db, path));
};

export const getControl = async (
  callback?: (control: IControl) => void,
  uuid?: string
): Promise<IControl | null> => {
  const isDummy = uuid?.includes('_dummy') || uuid?.includes('_partial');
  const path = getControlPath(uuid?.replace('_dummy', '').replace('_partial', '').replace('_inversed', ''));
  const convert = (control: IControl): IControl => {
    if (control && isDummy) {
      const dummyNicknameSource = selectedConfig?.staticUrl || selectedConfig?.name || control.teamNickname || control.teamName;
      control = {
        ...control,
        ...selectedConfig,
        teamName: selectedConfig?.name || control.teamName,
        teamNickname: `${dummyNicknameSource}-dummy`,
        teamPhoto: '',
        honours: '',
      };
    }
    return control;
  }
  let control = await getDbData<IControl>(
    path,
    () => ref(getDatabase(), path),
    (c) => callback?.(convert(c))
  );

  return control;
};
export const unsubscribeControl = (callback: (control: IControl) => void) => {
  const path = getControlPath();
  unsubscribeDbData<IControl>(path, callback);
};

// ---- Club Functions ----

const getConfigClubsPath = (): string => `config/clubs`;
const getClubConfigPath = (uuid: string): string => `config/clubs/${uuid}`;
const getClubControlPath = (uuid: string): string => `clubs/${uuid}/control`;
const getClubBlogPostsPath = (uuid: string): string => `clubs/${uuid}/blogPosts`;
const getClubSupportersPath = (uuid: string): string => `clubs/${uuid}/supporters`;
const getClubFollowRequestsPath = (uuid: string): string => `clubs/${uuid}/followRequests`;
const getClubUrlsPath = (): string => `config/clubUrls`;
const getClubUrlChildPath = (slug: string): string => `config/clubUrls/${slug}`;

export const getConfiguredClubs = async (oneTimeFetch: boolean = false): Promise<Record<string, ClubConfig> | null> => {
  const path = getConfigClubsPath();
  const fetchFn = oneTimeFetch ? getDbDataOnce : getDbData;
  const clubs = await fetchFn<Record<string, ClubConfig> | null>(
    path,
    () => ref(getDatabase(), path),
  );
  return clubs ?? {};
};

export const writeConfiguredClub = (club: ClubConfig): void => {
  if (!club.uuid) return;
  const db = getDatabase();
  const path = getClubConfigPath(club.uuid);
  setDbData(path, club, () => ref(db, path));
};

export const getClubControl = (
  uuid: string,
  callback?: (control: IClubControl) => void
): Promise<IClubControl | null> => {
  const path = getClubControlPath(uuid);
  return getDbData<IClubControl>(
    path,
    () => ref(getDatabase(), path),
    callback
  );
};

export const unsubscribeClubControl = (uuid: string, callback: (control: IClubControl) => void): void => {
  const path = getClubControlPath(uuid);
  unsubscribeDbData<IClubControl>(path, callback);
};

export const writeClubControl = async (uuid: string, control: IClubControl): Promise<void> => {
  const db = getDatabase();
  const path = getClubControlPath(uuid);
  const previous = await getDbDataOnce<IClubControl | null>(path, () => ref(db, path));
  const previousTeamIds = new Set(previous?.teamIds ?? []);
  const nextTeamIds = new Set(control.teamIds ?? []);

  await setDbData(path, control, () => ref(db, path));
  // Keep public config in sync
  const clubConfig: ClubConfig = {
    uuid: control.uuid,
    name: control.name,
    ownerUid: control.ownerUid,
    badge: control.badge,
    town: control.town,
    county: control.county,
    country: control.country,
    primaryColor: control.primaryColor,
    staticUrl: control.staticUrl,
  };
  writeConfiguredClub(clubConfig);

  const actor = getLoggedInUserName() || control.ownerName || 'Unknown user';
  if (!previous) {
    auditUpdate({
      action: 'Creating Club',
      user: senderId,
      details: `Created club ${control.name} (${uuid}) by ${actor}`,
      teamName: control.name,
      timestamp: new Date().toISOString(),
    });
  } else {
    auditUpdate({
      action: 'Updating Club',
      user: senderId,
      details: `Updating club ${control.name} (${uuid}) by ${actor}`,
      teamName: control.name,
      timestamp: new Date().toISOString(),
    });
  }

  const addedTeamIds = [...nextTeamIds].filter(teamId => !previousTeamIds.has(teamId));
  const removedTeamIds = [...previousTeamIds].filter(teamId => !nextTeamIds.has(teamId));
  addedTeamIds.forEach(teamId => {
    auditUpdate({
      action: 'Updating Club Team Links',
      user: senderId,
      details: `Linked team ${teamId} to club ${control.name} (${uuid}) by ${actor}`,
      teamName: control.name,
      timestamp: new Date().toISOString(),
    });
  });
  removedTeamIds.forEach(teamId => {
    auditUpdate({
      action: 'Updating Club Team Links',
      user: senderId,
      details: `Unlinked team ${teamId} from club ${control.name} (${uuid}) by ${actor}`,
      teamName: control.name,
      timestamp: new Date().toISOString(),
    });
  });
};

export const getClubBlogPosts = (
  uuid: string,
  callback?: (posts: IBlogPost[]) => void
): Promise<IBlogPost[] | null> => {
  const path = getClubBlogPostsPath(uuid);
  return getDbData<IBlogPost[]>(
    path,
    () => ref(getDatabase(), path),
    callback
  );
};

export const unsubscribeClubBlogPosts = (uuid: string, callback: (posts: IBlogPost[]) => void): void => {
  const path = getClubBlogPostsPath(uuid);
  unsubscribeDbData<IBlogPost[]>(path, callback);
};

export const writeClubBlogs = (uuid: string, posts: IBlogPost[]): Promise<void> => {
  const db = getDatabase();
  const path = getClubBlogPostsPath(uuid);
  return setDbData(path, posts, () => ref(db, path));
};

export const toggleClubBlogPostLike = async (clubUuid: string, postIndex: number, uid: string, liked: boolean, displayName?: string): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubBlogPostsPath(clubUuid)}/${postIndex}/likes/${uid}`;
  const namePath = `${getClubBlogPostsPath(clubUuid)}/${postIndex}/likeNames/${uid}`;
  if (liked) {
    await setDbData(path, true, () => ref(db, path));
    if (displayName) await setDbData(namePath, displayName, () => ref(db, namePath));
  } else {
    await removeDbData(path, () => ref(db, path));
    await removeDbData(namePath, () => ref(db, namePath));
  }
};

export const addClubBlogPostComment = async (clubUuid: string, postIndex: number, comment: IBlogComment): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubBlogPostsPath(clubUuid)}/${postIndex}/comments`;
  const comments = await getDbDataOnce<IBlogComment[]>(path, () => ref(db, path)) ?? [];
  const updated = Array.isArray(comments) ? [...comments, comment] : [...Object.values(comments), comment];
  await setDbData(path, updated, () => ref(db, path));
};

export const getClubSupporters = (
  uuid: string,
  callback?: (supporters: Record<string, ISupporterRecord>) => void,
  oneTimeFetch: boolean = false
): Promise<Record<string, ISupporterRecord> | null> => {
  const path = getClubSupportersPath(uuid);
  const fetchFn = (oneTimeFetch && !callback) ? getDbDataOnce : getDbData;
  return fetchFn<Record<string, ISupporterRecord>>(
    path,
    () => ref(getDatabase(), path),
    callback,
    getSupportersSentinelPath(uuid)
  );
};

export const unsubscribeClubSupporters = (uuid: string, callback: (supporters: Record<string, ISupporterRecord>) => void): void => {
  const path = getClubSupportersPath(uuid);
  unsubscribeDbData<Record<string, ISupporterRecord>>(path, callback);
};

export const writeClubSupporter = (clubUuid: string, supporterName: string, uid: string, email: string): void => {
  const db = getDatabase();
  const base = `${getClubSupportersPath(clubUuid)}/${uid}`;
  setDbData(`${base}/supporterName`, supporterName, () => ref(db, `${base}/supporterName`));
  setDbData(`${base}/uid`, uid, () => ref(db, `${base}/uid`));
  setDbData(`${base}/email`, email, () => ref(db, `${base}/email`));
  setDbData(`${base}/lastUpdated`, new Date().toISOString(), () => ref(db, `${base}/lastUpdated`));
};

export const updateClubSupporter = (clubUuid: string, uid: string, info: Partial<ISupporterRecord>): void => {
  const db = getDatabase();
  const path = `${getClubSupportersPath(clubUuid)}/${uid}`;
  setDbData(path, info, () => ref(db, path));
};

export const deleteClubSupporter = (clubUuid: string, uid: string): void => {
  const db = getDatabase();
  const path = `${getClubSupportersPath(clubUuid)}/${uid}`;
  removeDbData(path, () => ref(db, path));
};

export const getClubSupporterRecord = (clubUuid: string, uid: string): Promise<ISupporterRecord | null> => {
  const path = `${getClubSupportersPath(clubUuid)}/${uid}`;
  return getDbDataOnce<ISupporterRecord | null>(path, () => ref(getDatabase(), path));
};

export const getClubFollowRequests = (
  uuid: string,
  callback?: (requests: Record<string, boolean> | null) => void,
  oneTimeFetch: boolean = false
): Promise<Record<string, boolean> | null> => {
  const path = getClubFollowRequestsPath(uuid);
  const fetchFn = (oneTimeFetch && !callback) ? getDbDataOnce : getDbData;
  return fetchFn<Record<string, boolean>>(
    path,
    () => ref(getDatabase(), path),
    callback
  );
};

export const writeClubFollowRequest = (clubUuid: string, userUid: string, approved: boolean): void => {
  const db = getDatabase();
  const path = `${getClubFollowRequestsPath(clubUuid)}/${userUid}`;
  setDbData(path, approved, () => ref(db, path));
};

export const deleteClubFollowRequest = (clubUuid: string, userUid: string): void => {
  const db = getDatabase();
  const path = `${getClubFollowRequestsPath(clubUuid)}/${userUid}`;
  removeDbData(path, () => ref(db, path));
};

export const getClubShortcutUrls = (): Promise<Record<string, string>> => {
  const path = getClubUrlsPath();
  return getDbData<Record<string, string>>(
    path,
    () => ref(getDatabase(), path),
  ) as Promise<Record<string, string>>;
};

export const writeClubShortcutUrl = (slug: string, uuid: string): void => {
  const db = getDatabase();
  const path = getClubUrlChildPath(slug);
  setDbData(path, uuid, () => ref(db, path));
};

export const deleteClubShortcutUrl = (slug: string): void => {
  const db = getDatabase();
  const path = getClubUrlChildPath(slug);
  removeDbData(path, () => ref(db, path));
};

export const getClubUuidForSlug = (slug: string): Promise<string | null> => {
  const path = getClubUrlChildPath(slug);
  return getDbData<string | null>(
    path,
    () => ref(getDatabase(), path),
  );
};

// ---- Club Polls ----
const getClubPollsPath = (uuid: string): string => `clubs/${uuid}/polls`;

export const getClubPolls = (
  clubUuid: string,
  callback?: (polls: IPoll[]) => void
): Promise<IPoll[] | null> => {
  const path = getClubPollsPath(clubUuid);
  return getDbData<IPoll[]>(path, () => ref(getDatabase(), path), callback);
};

export const unsubscribeClubPolls = (clubUuid: string, callback: (polls: IPoll[]) => void): void => {
  const path = getClubPollsPath(clubUuid);
  unsubscribeDbData<IPoll[]>(path, callback);
};

export const writeClubPoll = async (clubUuid: string, poll: IPoll): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubPollsPath(clubUuid)}/${poll.id}`;
  await setDbData(path, poll, () => ref(db, path));
};

export const deleteClubPoll = async (clubUuid: string, pollId: string): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubPollsPath(clubUuid)}/${pollId}`;
  await removeDbData(path, () => ref(db, path));
};

// ---- Club Home Posts ----
const getClubHomePostsPath = (uuid: string): string => `clubs/${uuid}/homePosts`;

export const getClubHomePosts = (
  clubUuid: string,
  callback?: (posts: IBlogPost[]) => void
): Promise<IBlogPost[] | null> => {
  const path = getClubHomePostsPath(clubUuid);
  return getDbData<IBlogPost[]>(path, () => ref(getDatabase(), path), callback);
};

export const writeClubHomePosts = async (clubUuid: string, posts: IBlogPost[]): Promise<void> => {
  const db = getDatabase();
  const path = getClubHomePostsPath(clubUuid);
  const existingControl = await getDbDataOnce<IClubControl | null>(
    getClubControlPath(clubUuid),
    () => ref(getDatabase(), getClubControlPath(clubUuid))
  );
  const actor = getLoggedInUserName() || 'Unknown user';
  const clubName = existingControl?.name || clubUuid;
  auditUpdate({
    action: 'Updating Club Home',
    user: senderId,
    details: `Updating club home for ${clubName} (${clubUuid}) with ${posts.length} posts by ${actor}`,
    teamName: clubName,
    timestamp: new Date().toISOString(),
  });
  return setDbData(path, posts, () => ref(db, path));
};

export const toggleClubHomePostLike = async (clubUuid: string, postIndex: number, uid: string, liked: boolean): Promise<void> => {
  const db = getDatabase();
  const path = `clubs/${clubUuid}/homePosts/${postIndex}/likes/${uid}`;
  if (liked) {
    await setDbData(path, true, () => ref(db, path));
  } else {
    await removeDbData(path, () => ref(db, path));
  }
};

export const addClubHomePostComment = async (clubUuid: string, postIndex: number, comment: IBlogComment): Promise<void> => {
  const db = getDatabase();
  const path = `clubs/${clubUuid}/homePosts/${postIndex}/comments`;
  const comments = await getDbDataOnce<IBlogComment[]>(path, () => ref(db, path)) ?? [];
  const updated = Array.isArray(comments) ? [...comments, comment] : [...Object.values(comments), comment];
  await setDbData(path, updated, () => ref(db, path));
};

// ---- Club Pitch Booking ----
const getClubPitchesPath = (uuid: string): string => `clubs/${uuid}/pitches`;
const getClubPitchBookingsPath = (uuid: string): string => `clubs/${uuid}/pitchBookings`;
const getClubPitchSlotsPath = (uuid: string): string => `clubs/${uuid}/pitchSlots`;

export const getClubPitches = (
  clubUuid: string,
  callback?: (pitches: Record<string, IClubPitch>) => void
): Promise<Record<string, IClubPitch> | null> => {
  const path = getClubPitchesPath(clubUuid);
  return getDbData<Record<string, IClubPitch>>(path, () => ref(getDatabase(), path), callback);
};

export const writeClubPitch = async (clubUuid: string, pitch: IClubPitch): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubPitchesPath(clubUuid)}/${pitch.id}`;
  await setDbData(path, pitch, () => ref(db, path));
};

export const deleteClubPitch = async (clubUuid: string, pitchId: string): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubPitchesPath(clubUuid)}/${pitchId}`;
  await removeDbData(path, () => ref(db, path));
};

export const getClubPitchBookings = (
  clubUuid: string,
  callback?: (bookings: Record<string, IClubPitchBooking>) => void
): Promise<Record<string, IClubPitchBooking> | null> => {
  const path = getClubPitchBookingsPath(clubUuid);
  return getDbData<Record<string, IClubPitchBooking>>(path, () => ref(getDatabase(), path), callback);
};

export const unsubscribeClubPitchBookings = (
  clubUuid: string,
  callback: (bookings: Record<string, IClubPitchBooking>) => void
): void => {
  const path = getClubPitchBookingsPath(clubUuid);
  unsubscribeDbData<Record<string, IClubPitchBooking>>(path, callback);
};

export const writeClubPitchBooking = async (clubUuid: string, booking: IClubPitchBooking): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubPitchBookingsPath(clubUuid)}/${booking.id}`;
  await setDbData(path, booking, () => ref(db, path));
};

export const deleteClubPitchBooking = async (clubUuid: string, bookingId: string): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubPitchBookingsPath(clubUuid)}/${bookingId}`;
  await removeDbData(path, () => ref(db, path));
};

export const prunePastClubPitchBookings = async (clubUuid: string): Promise<number> => {
  const db = getDatabase();
  const path = getClubPitchBookingsPath(clubUuid);
  const bookings = await getDbDataOnce<Record<string, IClubPitchBooking> | null>(
    path,
    () => ref(db, path)
  );

  const now = Date.now();
  let removed = 0;
  const all = bookings ?? {};

  for (const booking of Object.values(all)) {
    const end = new Date(`${booking.date}T${booking.endTime}`).getTime();
    if (Number.isFinite(end) && end < now) {
      const bookingPath = `${path}/${booking.id}`;
      await removeDbData(bookingPath, () => ref(db, bookingPath));
      removed += 1;
    }
  }

  return removed;
};

// ---- Club Pitch Slots ----
export const getClubPitchSlots = (
  clubUuid: string,
  callback?: (slots: Record<string, IClubPitchSlot>) => void
): Promise<Record<string, IClubPitchSlot> | null> => {
  const path = getClubPitchSlotsPath(clubUuid);
  return getDbData<Record<string, IClubPitchSlot>>(path, () => ref(getDatabase(), path), callback);
};

export const unsubscribeClubPitchSlots = (
  clubUuid: string,
  callback: (slots: Record<string, IClubPitchSlot>) => void
): void => {
  const path = getClubPitchSlotsPath(clubUuid);
  unsubscribeDbData<Record<string, IClubPitchSlot>>(path, callback);
};

export const writeClubPitchSlot = async (clubUuid: string, slot: IClubPitchSlot): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubPitchSlotsPath(clubUuid)}/${slot.id}`;
  await setDbData(path, slot, () => ref(db, path));
};

export const deleteClubPitchSlot = async (clubUuid: string, slotId: string): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubPitchSlotsPath(clubUuid)}/${slotId}`;
  await removeDbData(path, () => ref(db, path));
};

// ---- Club About Pages ----
const getClubAboutPath = (uuid: string): string => `clubs/${uuid}/about`;

export const getClubAboutPages = (clubUuid: string): Promise<Record<string, IAboutPage> | null> => {
  const db = getDatabase();
  const path = getClubAboutPath(clubUuid);
  return getDbDataOnce<Record<string, IAboutPage>>(path, () => ref(db, path));
};

export const writeClubAboutPage = (clubUuid: string, page: IAboutPage): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubAboutPath(clubUuid)}/${page.id}`;
  return setDbData(path, page, () => ref(db, path));
};

export const deleteClubAboutPage = (clubUuid: string, pageId: string): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubAboutPath(clubUuid)}/${pageId}`;
  return removeDbData(path, () => ref(db, path));
};

// ---- Club Links ----
const getClubLinksPath = (uuid: string): string => `clubs/${uuid}/links`;

export const getClubLinks = (clubUuid: string): Promise<Record<string, IClubLink> | null> => {
  const db = getDatabase();
  const path = getClubLinksPath(clubUuid);
  return getDbDataOnce<Record<string, IClubLink>>(path, () => ref(db, path));
};

export const writeClubLink = (clubUuid: string, link: IClubLink): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubLinksPath(clubUuid)}/${link.id}`;
  return setDbData(path, link, () => ref(db, path));
};

export const deleteClubLink = (clubUuid: string, linkId: string): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubLinksPath(clubUuid)}/${linkId}`;
  return removeDbData(path, () => ref(db, path));
};

const supporterCallbackWrappers = new Map<
  (supporters: Record<string, ISupporterRecord>) => void,
  (supporters: Record<string, ISupporterRecord>) => void
>();

export const getSupporters = async (
  callback?: (supporters: Record<string, ISupporterRecord>) => void,
  uuid?: string,
  oneTimeFetch: boolean = false
): Promise<Record<string, ISupporterRecord> | null> => {
  const path = getSupportersPath(uuid);
  const fetchFn = (oneTimeFetch && !callback) ? getDbDataOnce : getDbData;
  const filteredCallback = callback
    ? ((supporters: Record<string, ISupporterRecord>) => callback(filterSupporterRecords(supporters)))
    : undefined;
  if (callback && filteredCallback) supporterCallbackWrappers.set(callback, filteredCallback);

  const supporters = await fetchFn<Record<string, ISupporterRecord>>(
    path,
    () => ref(getDatabase(), path),
    filteredCallback
  );
  return supporters ? filterSupporterRecords(supporters) : supporters;
};
export const unsubscribeSupporters = (callback: (supporters: Record<string, ISupporterRecord>) => void) => {
  const path = getSupportersPath();
  const filteredCallback = supporterCallbackWrappers.get(callback) ?? callback;
  unsubscribeDbData<Record<string, ISupporterRecord>>(path, filteredCallback);
  supporterCallbackWrappers.delete(callback);
};

export const getPlayers = async (
  callback?: (players: Record<string, IPlayerStats>) => void,
  uuid?: string,
  oneTimeFetch: boolean = false
): Promise<Record<string, IPlayerStats> | null> => {
  const path = getPlayersPath(uuid?.replace('_dummy', '').replace('_partial', '').replace('_inversed', ''));
  const fetchFn = (oneTimeFetch && !callback) ? getDbDataOnce : getDbData;

  return fetchFn<Record<string, IPlayerStats>>(
    path,
    () => ref(getDatabase(), path),
    callback,
    getPlayersSentinelPath(uuid?.replace('_dummy', '').replace('_partial', '').replace('_inversed', ''))
  );
};

export const unsubscribePlayers = (callback: (players: Record<string, IPlayerStats>) => void, uuid?: string) => {
  const path = getPlayersPath(uuid);
  unsubscribeDbData<Record<string, IPlayerStats>>(path, callback);
};

const getTicketPath = (id: string) => `tickets/${id}`;
export const getTicket = (id: string): Promise<ITicket | null> => {
  const path = getTicketPath(id);
  return getDbData<ITicket>(
    path,
    () => ref(getDatabase(), path),
    undefined
  );
};

const getTicketsPath = () => `tickets`;
export const getTickets = (callback?: (udates: Record<string, ITicket>) => void, oneTimeFetch: boolean = false): Promise<Record<string, ITicket> | null> => {
  const path = getTicketsPath();
  const fetchFn = (oneTimeFetch && !callback) ? getDbDataOnce : getDbData;
  return fetchFn<Record<string, ITicket>>(
    path,
    () => ref(getDatabase(), path),
    callback
  );
};

// ---------------------------------------------------------------------------
// Payments
// ---------------------------------------------------------------------------
function getPaymentRequestsPath(): string {
  return `teams/${selectedConfig?.uuid}/paymentRequests`;
}
function getPaymentTransactionsPath(): string {
  return `teams/${selectedConfig?.uuid}/paymentTransactions`;
}

export const getPaymentRequests = (
  callback?: (requests: Record<string, IPaymentRequest> | null) => void
): Promise<Record<string, IPaymentRequest> | null> => {
  const path = getPaymentRequestsPath();
  return getDbData<Record<string, IPaymentRequest>>(
    path,
    () => ref(getDatabase(), path),
    callback
  );
};

export const unsubscribePaymentRequests = (callback: (requests: Record<string, IPaymentRequest> | null) => void) => {
  const path = getPaymentRequestsPath();
  unsubscribeDbData<Record<string, IPaymentRequest>>(path, callback);
};

export const writePaymentRequest = async (request: IPaymentRequest): Promise<void> => {
  if (!selectedConfig) return;
  const db = getDatabase();
  const path = `${getPaymentRequestsPath()}/${request.id}`;
  await setDbData(path, request, () => ref(db, path));
};

export const deletePaymentRequest = async (requestId: string): Promise<void> => {
  if (!selectedConfig) return;
  const db = getDatabase();
  const path = `${getPaymentRequestsPath()}/${requestId}`;
  await removeDbData(path, () => ref(db, path));
};

export const getPaymentTransactions = (
  callback?: (transactions: Record<string, ITransaction> | null) => void
): Promise<Record<string, ITransaction> | null> => {
  const path = getPaymentTransactionsPath();
  return getDbData<Record<string, ITransaction>>(
    path,
    () => ref(getDatabase(), path),
    callback
  );
};

export const unsubscribePaymentTransactions = (callback: (transactions: Record<string, ITransaction> | null) => void) => {
  const path = getPaymentTransactionsPath();
  unsubscribeDbData<Record<string, ITransaction>>(path, callback);
};

export const writePaymentTransaction = async (transaction: ITransaction): Promise<void> => {
  if (!selectedConfig) return;
  const db = getDatabase();
  const path = `${getPaymentTransactionsPath()}/${transaction.id}`;
  await setDbData(path, transaction, () => ref(db, path));
};

export const deletePaymentTransaction = async (transactionId: string): Promise<void> => {
  if (!selectedConfig) return;
  const db = getDatabase();
  const path = `${getPaymentTransactionsPath()}/${transactionId}`;
  await removeDbData(path, () => ref(db, path));
};

// ── Club Payment Setups ──────────────────────────────────────────────────────
const getClubPaymentSetupsPath = (clubUuid: string) => `clubs/${clubUuid}/paymentSetups`;
const getClubMemberPaymentsPath = (clubUuid: string) => `clubs/${clubUuid}/memberPayments`;
const getClubDiscountLinksPath = (clubUuid: string) => `clubs/${clubUuid}/discountLinks`;

export const getClubPaymentSetups = (
  clubUuid: string,
  callback?: (setups: Record<string, IClubPaymentSetup> | null) => void
): Promise<Record<string, IClubPaymentSetup> | null> => {
  const path = getClubPaymentSetupsPath(clubUuid);
  return getDbData<Record<string, IClubPaymentSetup>>(
    path,
    () => ref(getDatabase(), path),
    callback
  );
};

export const unsubscribeClubPaymentSetups = (
  clubUuid: string,
  callback: (setups: Record<string, IClubPaymentSetup> | null) => void
) => {
  const path = getClubPaymentSetupsPath(clubUuid);
  unsubscribeDbData<Record<string, IClubPaymentSetup>>(path, callback);
};

export const writeClubPaymentSetup = async (clubUuid: string, setup: IClubPaymentSetup): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubPaymentSetupsPath(clubUuid)}/${setup.id}`;
  await setDbData(path, setup, () => ref(db, path));
};

export const deleteClubPaymentSetup = async (clubUuid: string, setupId: string): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubPaymentSetupsPath(clubUuid)}/${setupId}`;
  await removeDbData(path, () => ref(db, path));
};

// ── Club Member Payments ─────────────────────────────────────────────────────
export const getClubMemberPaymentsBySetup = (
  clubUuid: string,
  setupId: string,
  callback?: (payments: Record<string, IClubMemberPayment> | null) => void
): Promise<Record<string, IClubMemberPayment> | null> => {
  const path = `${getClubMemberPaymentsPath(clubUuid)}/${setupId}`;
  return getDbData<Record<string, IClubMemberPayment>>(
    path,
    () => ref(getDatabase(), path),
    callback
  );
};

export const unsubscribeClubMemberPaymentsBySetup = (
  clubUuid: string,
  setupId: string,
  callback: (payments: Record<string, IClubMemberPayment> | null) => void
) => {
  const path = `${getClubMemberPaymentsPath(clubUuid)}/${setupId}`;
  unsubscribeDbData<Record<string, IClubMemberPayment>>(path, callback);
};

export const writeClubMemberPayment = async (
  clubUuid: string,
  setupId: string,
  payment: IClubMemberPayment
): Promise<void> => {
  const db = getDatabase();
  // Keyed by payment.id so multiple payments per member are supported
  const path = `${getClubMemberPaymentsPath(clubUuid)}/${setupId}/${payment.id}`;
  await setDbData(path, payment, () => ref(db, path));
};

export const deleteClubMemberPayment = (
  clubUuid: string,
  setupId: string,
  paymentId: string
): void => {
  const db = getDatabase();
  const path = `${getClubMemberPaymentsPath(clubUuid)}/${setupId}/${paymentId}`;
  removeDbData(path, () => ref(db, path));
};

export const getClubMemberPaymentsByUser = async (
  clubUuid: string,
  uid: string
): Promise<IClubMemberPayment[]> => {
  const allSetups = await getDbDataOnce<Record<string, Record<string, IClubMemberPayment>>>(
    getClubMemberPaymentsPath(clubUuid),
    () => ref(getDatabase(), getClubMemberPaymentsPath(clubUuid))
  );
  if (!allSetups) return [];
  const results: IClubMemberPayment[] = [];
  for (const setupPayments of Object.values(allSetups)) {
    const matched = Object.values(setupPayments ?? {}).filter((p) => p.uid === uid);
    results.push(...matched);
  }
  return results;
};

// ── Club Discount Links ──────────────────────────────────────────────────────
export const getClubDiscountLink = async (
  clubUuid: string,
  linkId: string
): Promise<IClubDiscountLink | null> => {
  const path = `${getClubDiscountLinksPath(clubUuid)}/${linkId}`;
  return getDbDataOnce<IClubDiscountLink>(path, () => ref(getDatabase(), path));
};

export const writeClubDiscountLink = async (
  clubUuid: string,
  link: IClubDiscountLink
): Promise<void> => {
  const db = getDatabase();
  const path = `${getClubDiscountLinksPath(clubUuid)}/${link.id}`;
  await setDbData(path, link, () => ref(db, path));
};

/**
 * Atomically marks a discount link as consumed using a Firebase transaction.
 * Returns true if this call consumed the link, false if already consumed or not found.
 */
export const consumeClubDiscountLink = async (
  clubUuid: string,
  linkId: string,
  uid: string
): Promise<boolean> => {
  const db = getDatabase();
  const path = `${getClubDiscountLinksPath(clubUuid)}/${linkId}`;
  const { runTransaction } = await import('firebase/database');
  let wasConsumed = false;
  await runTransaction(ref(db, path), (current: IClubDiscountLink | null) => {
    if (!current || current.consumed) {
      return; // abort — link missing or already used
    }
    wasConsumed = true;
    return {
      ...current,
      consumed: true,
      consumedAt: new Date().toISOString(),
      consumedBy: uid,
      useCount: (current.useCount ?? 0) + 1,
    };
  });
  return wasConsumed;
};

// ── Cross-team helpers for profile view ─────────────────────────────────────
/** Fetches all payment transactions for a given team UUID filtered to a specific user. */
export const getTeamPaymentTransactionsForUser = async (
  teamUuid: string,
  uid: string
): Promise<ITransaction[]> => {
  const path = `teams/${teamUuid}/paymentTransactions`;
  const all = await getDbDataOnce<Record<string, ITransaction>>(
    path,
    () => ref(getDatabase(), path)
  );
  if (!all) return [];
  return Object.values(all).filter((t) => t.uid === uid);
};

// ── Tactical Videos ─────────────────────────────────────────────────────────
const getTacticalVideosPath = (uuid?: string): string =>
  `teams/${uuid ?? selectedConfig?.uuid}/tacticalVideos`;

export const getTacticalVideos = (
  callback?: (videos: Record<string, ITacticalVideo>) => void,
  uuid?: string
): Promise<Record<string, ITacticalVideo> | null> => {
  const path = getTacticalVideosPath(uuid);
  return getDbData<Record<string, ITacticalVideo>>(
    path,
    () => ref(getDatabase(), path),
    callback
  );
};

export const unsubscribeTacticalVideos = (
  callback: (videos: Record<string, ITacticalVideo>) => void,
  uuid?: string
): void => {
  const path = getTacticalVideosPath(uuid);
  unsubscribeDbData<Record<string, ITacticalVideo>>(path, callback);
};

export const writeTacticalVideo = async (video: ITacticalVideo, uuid?: string): Promise<void> => {
  const targetUuid = uuid ?? selectedConfig?.uuid;
  if (!targetUuid) {
    console.error('Cannot write tactical video without selected team');
    return;
  }
  const trimmedDownloadUrl = typeof video.downloadUrl === 'string' ? video.downloadUrl.trim() : '';
  if (!trimmedDownloadUrl) {
    console.error('Cannot write tactical video without a valid download URL');
    return;
  }
  const trimmedThumbnailUrl = typeof video.thumbnailUrl === 'string' ? video.thumbnailUrl.trim() : '';
  const normalizedVideo: ITacticalVideo = {
    ...video,
    downloadUrl: trimmedDownloadUrl,
    thumbnailUrl: trimmedThumbnailUrl || undefined,
  };
  const db = getDatabase();
  const path = `${getTacticalVideosPath(targetUuid)}/${video.id}`;
  await setDbData(path, normalizedVideo, () => ref(db, path));
};

export const deleteTacticalVideo = async (videoId: string, uuid?: string): Promise<void> => {
  const targetUuid = uuid ?? selectedConfig?.uuid;
  if (!targetUuid) {
    console.error('Cannot delete tactical video without selected team');
    return;
  }
  const db = getDatabase();
  const path = `${getTacticalVideosPath(targetUuid)}/${videoId}`;
  await removeDbData(path, () => ref(db, path));
};

// ── Tactics Boards ───────────────────────────────────────────────────────────

const getTacticsBoardsPath = (uuid?: string): string =>
  `teams/${uuid ?? selectedConfig?.uuid}/tacticsBoards`;

export const getTacticsBoards = (
  callback?: (boards: Record<string, ITacticsBoard>) => void,
  uuid?: string
): Promise<Record<string, ITacticsBoard> | null> => {
  const path = getTacticsBoardsPath(uuid);
  return getDbData<Record<string, ITacticsBoard>>(
    path,
    () => ref(getDatabase(), path),
    callback
  );
};

export const unsubscribeTacticsBoards = (
  callback: (boards: Record<string, ITacticsBoard>) => void,
  uuid?: string
): void => {
  const path = getTacticsBoardsPath(uuid);
  unsubscribeDbData<Record<string, ITacticsBoard>>(path, callback);
};

export const writeTacticsBoard = async (board: ITacticsBoard): Promise<void> => {
  if (!selectedConfig) {
    console.error('Cannot write tactics board without selected team');
    return;
  }
  const db = getDatabase();
  const path = `${getTacticsBoardsPath()}/${board.id}`;
  await setDbData(path, board, () => ref(db, path));
};

export const deleteTacticsBoard = async (boardId: string): Promise<void> => {
  if (!selectedConfig) {
    console.error('Cannot delete tactics board without selected team');
    return;
  }
  const db = getDatabase();
  const path = `${getTacticsBoardsPath()}/${boardId}`;
  await removeDbData(path, () => ref(db, path));
};

/** Fetches all payment requests for a given team UUID. */
export const getTeamPaymentRequestsOnce = async (
  teamUuid: string
): Promise<Record<string, IPaymentRequest> | null> => {
  const path = `teams/${teamUuid}/paymentRequests`;
  return getDbDataOnce<Record<string, IPaymentRequest>>(
    path,
    () => ref(getDatabase(), path)
  );
};

export const getArchivedPlayers = (tag: string, uuid?: string): Promise<Record<string, IPlayerStats> | null> => {
  const teamUuid = uuid || selectedConfig?.uuid;
  if (!teamUuid || !tag) return Promise.resolve(null);
  const path = `teams/${teamUuid}/archive/${tag}/players`;
  return getDbDataOnce<Record<string, IPlayerStats>>(path, () => ref(getDatabase(), path));
};

export const getArchivedResults = (tag: string, uuid?: string): Promise<ResultInstance[] | null> => {
  const teamUuid = uuid || selectedConfig?.uuid;
  if (!teamUuid || !tag) return Promise.resolve(null);
  const path = `teams/${teamUuid}/archive/${tag}/results`;
  return getDbDataOnce<ResultInstance[]>(path, () => ref(getDatabase(), path));
};

export const getDiaryAttendanceRecipientProjections = async (
  uid: string,
): Promise<Record<string, IDiaryAttendanceRecipientProjection> | null> => {
  const path = `diaryAttendanceRecipients/${uid}`;
  const value = await getDbDataOnce<Record<string, IDiaryAttendanceRecipientProjection>>(
    path,
    () => ref(getDatabase(), path),
  );
  return value && !Array.isArray(value) ? value : null;
};

export const getDiaryAttendanceRequest = async (
  teamUid: string,
  requestId: string,
): Promise<IDiaryAttendanceRequest | null> => {
  const path = `diaryAttendanceRequests/${teamUid}/${requestId}`;
  const value = await getDbDataOnce<IDiaryAttendanceRequest>(path, () => ref(getDatabase(), path));
  return value && !Array.isArray(value) ? value : null;
};

export const getDiaryAttendanceRequestsForTeam = (
  teamUid: string,
  callback?: (requests: Record<string, IDiaryAttendanceRequest> | null) => void,
): Promise<Record<string, IDiaryAttendanceRequest> | null> => {
  const path = `diaryAttendanceRequests/${teamUid}`;
  if (callback) {
    return getDbData<Record<string, IDiaryAttendanceRequest> | null>(
      path,
      () => ref(getDatabase(), path),
      callback,
    );
  }
  return getDbDataOnce<Record<string, IDiaryAttendanceRequest> | null>(path, () => ref(getDatabase(), path));
};

export const unsubscribeDiaryAttendanceRequests = (
  teamUid: string,
  callback: (requests: Record<string, IDiaryAttendanceRequest> | null) => void,
) => {
  unsubscribeDbData<Record<string, IDiaryAttendanceRequest> | null>(
    `diaryAttendanceRequests/${teamUid}`,
    callback,
  );
};

export const markDiaryAttendanceOpened = async (uid: string, requestId: string): Promise<void> => {
  const path = `diaryAttendanceRecipients/${uid}/${requestId}/openedAt`;
  await setDbData(path, Date.now(), () => ref(getDatabase(), path));
};

export const submitDiaryAttendanceResponse = async (
  uid: string,
  requestId: string,
  response: IDiaryAttendanceResponse,
): Promise<void> => {
  const path = `diaryAttendanceRecipients/${uid}/${requestId}/response`;
  await setDbData(path, response, () => ref(getDatabase(), path));
};

export type DiaryAttendanceManagementAction = "close" | "reopen" | "cancel" | "nudge" | "manualResponse";

export const manageDiaryAttendanceRequest = (
  teamUid: string,
  requestId: string,
  action: DiaryAttendanceManagementAction,
  details: { recipientUid?: string; status?: "attending" | "notAttending" | "maybe"; reason?: string; note?: string } = {},
): Promise<{ success: boolean; nudged?: number }> => callFirebaseFunction(
  "manageDiaryAttendanceRequest",
  { teamUid, requestId, action, ...details },
);